package com.mgacreative.hotelru.shared.ui.designsystem.i18n

import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow

enum class AppLanguage(val code: String, val displayName: String, val flag: String) {
    RU("ru", "Русский", "🇷🇺"),
    TR("tr", "Türkçe", "🇹🇷"),
    EN("en", "English", "🇺🇸"),
    DE("de", "Deutsch", "🇩🇪"),
    FR("fr", "Français", "🇫🇷"),
    ES("es", "Español", "🇪🇸");

    companion object {
        fun fromCode(code: String): AppLanguage {
            val clean = code.lowercase().take(2)
            return entries.find { it.code == clean } ?: RU // Default to RU
        }
    }
}

object AppLocalizationManager {
    // Default language is RU (Russian Federation release)
    private val _currentLanguage = MutableStateFlow(AppLanguage.RU)
    val currentLanguage: StateFlow<AppLanguage> = _currentLanguage.asStateFlow()

    init {
        // Auto detect system language
        detectAndSetSystemLanguage()
    }

    fun detectAndSetSystemLanguage(deviceLanguageCode: String? = null) {
        if (deviceLanguageCode != null) {
            _currentLanguage.value = AppLanguage.fromCode(deviceLanguageCode)
        }
    }

    fun setLanguage(language: AppLanguage) {
        _currentLanguage.value = language
    }

    private val translationDictionary: Map<String, Map<AppLanguage, String>> = mapOf(
        "Dashboard" to mapOf(
            AppLanguage.RU to "Панель управления",
            AppLanguage.TR to "Gösterge Paneli",
            AppLanguage.EN to "Dashboard"
        ),
        "New Reservation" to mapOf(
            AppLanguage.RU to "Новое бронирование",
            AppLanguage.TR to "Yeni Rezervasyon",
            AppLanguage.EN to "New Reservation"
        ),
        "Quick Check-in" to mapOf(
            AppLanguage.RU to "Быстрый заезд",
            AppLanguage.TR to "Hızlı Giriş",
            AppLanguage.EN to "Quick Check-in"
        ),
        "Quick Check-out" to mapOf(
            AppLanguage.RU to "Быстрый выезд",
            AppLanguage.TR to "Hızlı Çıkış",
            AppLanguage.EN to "Quick Check-out"
        ),
        "Expected Arrivals" to mapOf(
            AppLanguage.RU to "Ожидаемый заезд",
            AppLanguage.TR to "Giriş Beklenenler",
            AppLanguage.EN to "Expected Arrivals"
        ),
        "Expected Departures" to mapOf(
            AppLanguage.RU to "Ожидаемый выезд",
            AppLanguage.TR to "Çıkış Beklenenler",
            AppLanguage.EN to "Expected Departures"
        ),
        "Available Rooms" to mapOf(
            AppLanguage.RU to "Свободные номера",
            AppLanguage.TR to "Müsait Odalar",
            AppLanguage.EN to "Available Rooms"
        ),
        "Cleaning / Maintenance" to mapOf(
            AppLanguage.RU to "Уборка / Ремонт",
            AppLanguage.TR to "Temizlik / Bakım",
            AppLanguage.EN to "Cleaning / Maintenance"
        ),
        "Agency Allotment" to mapOf(
            AppLanguage.RU to "Квоты агентств",
            AppLanguage.TR to "Acenta Kontenjanı",
            AppLanguage.EN to "Agency Allotment"
        ),
        "Occupied Rooms" to mapOf(
            AppLanguage.RU to "Занятые номера",
            AppLanguage.TR to "Dolu Odalar",
            AppLanguage.EN to "Occupied Rooms"
        ),
        "Front Office" to mapOf(
            AppLanguage.RU to "Стойка регистрации",
            AppLanguage.TR to "Ön Büro",
            AppLanguage.EN to "Front Office"
        ),
        "Occupancy" to mapOf(
            AppLanguage.RU to "Загрузка",
            AppLanguage.TR to "Doluluk",
            AppLanguage.EN to "Occupancy"
        ),
        "Check-in" to mapOf(
            AppLanguage.RU to "Заезд",
            AppLanguage.TR to "Giriş",
            AppLanguage.EN to "Check-in"
        ),
        "Check-out" to mapOf(
            AppLanguage.RU to "Выезд",
            AppLanguage.TR to "Çıkış",
            AppLanguage.EN to "Check-out"
        ),
        "In Use" to mapOf(
            AppLanguage.RU to "Занятые",
            AppLanguage.TR to "Dolu Odalar",
            AppLanguage.EN to "In Use"
        ),
        "List" to mapOf(
            AppLanguage.RU to "Список",
            AppLanguage.TR to "Liste",
            AppLanguage.EN to "List"
        ),
        "Rooms" to mapOf(
            AppLanguage.RU to "Номера",
            AppLanguage.TR to "Odalar",
            AppLanguage.EN to "Rooms"
        ),
        "Tasks" to mapOf(
            AppLanguage.RU to "Задачи",
            AppLanguage.TR to "Görevler",
            AppLanguage.EN to "Tasks"
        ),
        "Cash" to mapOf(
            AppLanguage.RU to "Касса",
            AppLanguage.TR to "Kasa",
            AppLanguage.EN to "Cash"
        ),
        "Stock" to mapOf(
            AppLanguage.RU to "Склад",
            AppLanguage.TR to "Stok",
            AppLanguage.EN to "Stock"
        ),
        "Maintenance" to mapOf(
            AppLanguage.RU to "Ремонт",
            AppLanguage.TR to "Bakım",
            AppLanguage.EN to "Maintenance"
        ),
        "Reports" to mapOf(
            AppLanguage.RU to "Отчеты",
            AppLanguage.TR to "Raporlar",
            AppLanguage.EN to "Reports"
        ),
        "Staff" to mapOf(
            AppLanguage.RU to "Персонал",
            AppLanguage.TR to "Personel",
            AppLanguage.EN to "Staff"
        ),
        "Settings" to mapOf(
            AppLanguage.RU to "Настройки",
            AppLanguage.TR to "Ayarlar",
            AppLanguage.EN to "Settings"
        ),
        "Inventory Management" to mapOf(
            AppLanguage.RU to "Управление складом",
            AppLanguage.TR to "Envanter Yönetimi",
            AppLanguage.EN to "Inventory Management"
        ),
        "Total Item Types" to mapOf(
            AppLanguage.RU to "Всего наименований",
            AppLanguage.TR to "Toplam Ürün Çeşidi",
            AppLanguage.EN to "Total Item Types"
        ),
        "Critical Stock" to mapOf(
            AppLanguage.RU to "Критический остаток",
            AppLanguage.TR to "Kritik Stok",
            AppLanguage.EN to "Critical Stock"
        ),
        "Add New Item" to mapOf(
            AppLanguage.RU to "Добавить новый товар",
            AppLanguage.TR to "Yeni Ürün Ekle",
            AppLanguage.EN to "Add New Item"
        ),
        "Item Name" to mapOf(
            AppLanguage.RU to "Наименование товара",
            AppLanguage.TR to "Ürün Adı",
            AppLanguage.EN to "Item Name"
        ),
        "Category" to mapOf(
            AppLanguage.RU to "Категория",
            AppLanguage.TR to "Kategori",
            AppLanguage.EN to "Category"
        ),
        "Select Category" to mapOf(
            AppLanguage.RU to "Выберите категорию",
            AppLanguage.TR to "Kategori Seçin",
            AppLanguage.EN to "Select Category"
        ),
        "Initial Quantity" to mapOf(
            AppLanguage.RU to "Начальное количество",
            AppLanguage.TR to "Başlangıç Miktarı",
            AppLanguage.EN to "Initial Quantity"
        ),
        "Critical Threshold" to mapOf(
            AppLanguage.RU to "Критический порог",
            AppLanguage.TR to "Kritik Eşik",
            AppLanguage.EN to "Critical Threshold"
        ),
        "Add" to mapOf(
            AppLanguage.RU to "Добавить",
            AppLanguage.TR to "Ekle",
            AppLanguage.EN to "Add"
        ),
        "Cancel" to mapOf(
            AppLanguage.RU to "Отмена",
            AppLanguage.TR to "İptal",
            AppLanguage.EN to "Cancel"
        ),
        "Confirm" to mapOf(
            AppLanguage.RU to "Подтвердить",
            AppLanguage.TR to "Onayla",
            AppLanguage.EN to "Confirm"
        ),
        "Beverage" to mapOf(
            AppLanguage.RU to "Напитки",
            AppLanguage.TR to "İçecek",
            AppLanguage.EN to "Beverage"
        ),
        "Cleaning" to mapOf(
            AppLanguage.RU to "Уборка",
            AppLanguage.TR to "Temizlik",
            AppLanguage.EN to "Cleaning"
        ),
        "Textile" to mapOf(
            AppLanguage.RU to "Текстиль",
            AppLanguage.TR to "Tekstil",
            AppLanguage.EN to "Textile"
        ),
        "Food" to mapOf(
            AppLanguage.RU to "Продукты",
            AppLanguage.TR to "Gıda",
            AppLanguage.EN to "Food"
        ),
        "Stocks" to mapOf(
            AppLanguage.RU to "Склад",
            AppLanguage.TR to "Stoklar",
            AppLanguage.EN to "Stocks"
        ),
        "Order" to mapOf(
            AppLanguage.RU to "Заказы",
            AppLanguage.TR to "Sipariş",
            AppLanguage.EN to "Order"
        ),
        "Stock List" to mapOf(
            AppLanguage.RU to "Список товаров",
            AppLanguage.TR to "Stok Listesi",
            AppLanguage.EN to "Stock List"
        ),
        "Purchasing" to mapOf(
            AppLanguage.RU to "Закупки",
            AppLanguage.TR to "Satın Alma",
            AppLanguage.EN to "Purchasing"
        ),
        "In Stock" to mapOf(
            AppLanguage.RU to "В наличии",
            AppLanguage.TR to "Stokta Var",
            AppLanguage.EN to "In Stock"
        ),
        "Low Stock" to mapOf(
            AppLanguage.RU to "Мало",
            AppLanguage.TR to "Az Stok",
            AppLanguage.EN to "Low Stock"
        ),
        "Out of Stock" to mapOf(
            AppLanguage.RU to "Нет в наличии",
            AppLanguage.TR to "Tükendi",
            AppLanguage.EN to "Out of Stock"
        ),
        "Select" to mapOf(
            AppLanguage.RU to "Выбрать",
            AppLanguage.TR to "Seç",
            AppLanguage.EN to "Select"
        ),
        "Add Item" to mapOf(
            AppLanguage.RU to "Добавить товар",
            AppLanguage.TR to "Ürün Ekle",
            AppLanguage.EN to "Add Item"
        ),
        "Shift Plan" to mapOf(
            AppLanguage.RU to "План смен",
            AppLanguage.TR to "Vardiya Planı",
            AppLanguage.EN to "Shift Plan"
        ),
        "No shifts found for this day." to mapOf(
            AppLanguage.RU to "На этот день смен не найдено.",
            AppLanguage.TR to "Bu gün için vardiya bulunamadı.",
            AppLanguage.EN to "No shifts found for this day."
        ),
        "Add New Shift" to mapOf(
            AppLanguage.RU to "Добавить новую смену",
            AppLanguage.TR to "Yeni Vardiya Ekle",
            AppLanguage.EN to "Add New Shift"
        ),
        "Select Staff" to mapOf(
            AppLanguage.RU to "Выберите сотрудника",
            AppLanguage.TR to "Personel Seçin",
            AppLanguage.EN to "Select Staff"
        ),
        "Role (e.g. Reception)" to mapOf(
            AppLanguage.RU to "Должность (напр. Администратор)",
            AppLanguage.TR to "Rol (Örn. Resepsiyon)",
            AppLanguage.EN to "Role (e.g. Reception)"
        ),
        "Start (HH:MM)" to mapOf(
            AppLanguage.RU to "Начало (ЧЧ:ММ)",
            AppLanguage.TR to "Başlangıç (HH:MM)",
            AppLanguage.EN to "Start (HH:MM)"
        ),
        "End (HH:MM)" to mapOf(
            AppLanguage.RU to "Конец (ЧЧ:ММ)",
            AppLanguage.TR to "Bitiş (HH:MM)",
            AppLanguage.EN to "End (HH:MM)"
        ),
        "Shifts" to mapOf(
            AppLanguage.RU to "Смены",
            AppLanguage.TR to "Vardiyalar",
            AppLanguage.EN to "Shifts"
        ),
        "Staff List" to mapOf(
            AppLanguage.RU to "Список персонала",
            AppLanguage.TR to "Personel Listesi",
            AppLanguage.EN to "Staff List"
        ),
        "Add Shift" to mapOf(
            AppLanguage.RU to "Добавить смену",
            AppLanguage.TR to "Vardiya Ekle",
            AppLanguage.EN to "Add Shift"
        ),
        "Add Staff" to mapOf(
            AppLanguage.RU to "Добавить сотрудника",
            AppLanguage.TR to "Personel Ekle",
            AppLanguage.EN to "Add Staff"
        ),
        "No staff found yet." to mapOf(
            AppLanguage.RU to "Сотрудники пока не найдены.",
            AppLanguage.TR to "Henüz personel kaydı yok.",
            AppLanguage.EN to "No staff found yet."
        ),
        "Active" to mapOf(
            AppLanguage.RU to "Активно",
            AppLanguage.TR to "Aktif",
            AppLanguage.EN to "Active"
        ),
        "Pending" to mapOf(
            AppLanguage.RU to "В ожидании",
            AppLanguage.TR to "Beklemede",
            AppLanguage.EN to "Pending"
        ),
        "Previous" to mapOf(
            AppLanguage.RU to "Назад",
            AppLanguage.TR to "Önceki",
            AppLanguage.EN to "Previous"
        ),
        "Next" to mapOf(
            AppLanguage.RU to "Вперед",
            AppLanguage.TR to "Sonraki",
            AppLanguage.EN to "Next"
        ),
        "Restaurant POS" to mapOf(
            AppLanguage.RU to "POS Ресторан",
            AppLanguage.TR to "Restoran POS",
            AppLanguage.EN to "Restaurant POS"
        ),
        "Open Tables" to mapOf(
            AppLanguage.RU to "Открытые столы",
            AppLanguage.TR to "Açık Masalar",
            AppLanguage.EN to "Open Tables"
        ),
        "Tables Active" to mapOf(
            AppLanguage.RU to "Столов активно",
            AppLanguage.TR to "Masa Aktif",
            AppLanguage.EN to "Tables Active"
        ),
        "Open New Table" to mapOf(
            AppLanguage.RU to "Открыть новый стол",
            AppLanguage.TR to "Yeni Masa Aç",
            AppLanguage.EN to "Open New Table"
        ),
        "+ Open New Table" to mapOf(
            AppLanguage.RU to "+ Открыть новый стол",
            AppLanguage.TR to "+ Yeni Masa Aç",
            AppLanguage.EN to "+ Open New Table"
        ),
        "Table Name / No" to mapOf(
            AppLanguage.RU to "Название / № стола",
            AppLanguage.TR to "Masa Adı / No",
            AppLanguage.EN to "Table Name / No"
        ),
        "Open and Go to Adisyon" to mapOf(
            AppLanguage.RU to "Открыть и перейти к заказу",
            AppLanguage.TR to "Aç ve Adisyona Git",
            AppLanguage.EN to "Open and Go to Adisyon"
        ),
        "OPEN" to mapOf(
            AppLanguage.RU to "ОТКРЫТ",
            AppLanguage.TR to "AÇIK",
            AppLanguage.EN to "OPEN"
        ),
        "Add Manual Item / Service" to mapOf(
            AppLanguage.RU to "Добавить вручную",
            AppLanguage.TR to "Manuel Ürün / Hizmet Ekle",
            AppLanguage.EN to "Add Manual Item / Service"
        ),
        "Manual Add" to mapOf(
            AppLanguage.RU to "Вручную",
            AppLanguage.TR to "Manuel Ekle",
            AppLanguage.EN to "Manual Add"
        ),
        "Manual Add Item" to mapOf(
            AppLanguage.RU to "Добавить товар вручную",
            AppLanguage.TR to "Manuel Ürün Ekle",
            AppLanguage.EN to "Manual Add Item"
        ),
        "+ Manual Add Item" to mapOf(
            AppLanguage.RU to "+ Добавить товар вручную",
            AppLanguage.TR to "+ Manuel Ürün Ekle",
            AppLanguage.EN to "+ Manual Add Item"
        ),
        "Item / Service Name" to mapOf(
            AppLanguage.RU to "Название товара / услуги",
            AppLanguage.TR to "Ürün / Hizmet Adı",
            AppLanguage.EN to "Item / Service Name"
        ),
        "Price" to mapOf(
            AppLanguage.RU to "Цена",
            AppLanguage.TR to "Fiyat",
            AppLanguage.EN to "Price"
        ),
        "Change Table Name" to mapOf(
            AppLanguage.RU to "Изменить название стола",
            AppLanguage.TR to "Masa Adını Değiştir",
            AppLanguage.EN to "Change Table Name"
        ),
        "Table Name" to mapOf(
            AppLanguage.RU to "Название стола",
            AppLanguage.TR to "Masa Adı",
            AppLanguage.EN to "Table Name"
        ),
        "Payment Method" to mapOf(
            AppLanguage.RU to "Способ оплаты",
            AppLanguage.TR to "Ödeme Yöntemi",
            AppLanguage.EN to "Payment Method"
        ),
        "Cash / Credit Card" to mapOf(
            AppLanguage.RU to "Наличные / Карта",
            AppLanguage.TR to "Nakit / Kredi Kartı",
            AppLanguage.EN to "Cash / Credit Card"
        ),
        "Charge to Room" to mapOf(
            AppLanguage.RU to "Записать на номер",
            AppLanguage.TR to "Odaya Yaz (Folyo)",
            AppLanguage.EN to "Charge to Room"
        ),
        "Room Selection" to mapOf(
            AppLanguage.RU to "Выбор номера",
            AppLanguage.TR to "Oda Seçimi",
            AppLanguage.EN to "Room Selection"
        ),
        "Search room no or guest name..." to mapOf(
            AppLanguage.RU to "Поиск по номеру или имени...",
            AppLanguage.TR to "Oda no veya misafir adı ara...",
            AppLanguage.EN to "Search room no or guest name..."
        ),
        "No staying guests found." to mapOf(
            AppLanguage.RU to "Проживающих гостей не найдено.",
            AppLanguage.TR to "Konaklayan misafir bulunamadı.",
            AppLanguage.EN to "No staying guests found."
        ),
        "No matching room found." to mapOf(
            AppLanguage.RU to "Подходящих номеров не найдено.",
            AppLanguage.TR to "Eşleşen oda bulunamadı.",
            AppLanguage.EN to "No matching room found."
        ),
        "Guest" to mapOf(
            AppLanguage.RU to "Гость",
            AppLanguage.TR to "Misafir",
            AppLanguage.EN to "Guest"
        ),
        "Room" to mapOf(
            AppLanguage.RU to "Номер",
            AppLanguage.TR to "Oda",
            AppLanguage.EN to "Room"
        ),
        "Complete Payment" to mapOf(
            AppLanguage.RU to "Завершить оплату",
            AppLanguage.TR to "Ödemeyi Tamamla",
            AppLanguage.EN to "Complete Payment"
        ),
        "Payment successfully received and processed." to mapOf(
            AppLanguage.RU to "Оплата успешно принята и проведена.",
            AppLanguage.TR to "Ödeme başarıyla alındı ve işlendi.",
            AppLanguage.EN to "Payment successfully received and processed."
        ),
        "Hold & Close" to mapOf(
            AppLanguage.RU to "Сохранить и закрыть",
            AppLanguage.TR to "Beklet & Kapat",
            AppLanguage.EN to "Hold & Close"
        ),
        "Close" to mapOf(
            AppLanguage.RU to "Закрыть",
            AppLanguage.TR to "Kapat",
            AppLanguage.EN to "Close"
        ),
        "Order Adisyon" to mapOf(
            AppLanguage.RU to "Счет / Заказ",
            AppLanguage.TR to "Sipariş Adisyonu",
            AppLanguage.EN to "Order Adisyon"
        ),
        "All" to mapOf(
            AppLanguage.RU to "Все",
            AppLanguage.TR to "Tümü",
            AppLanguage.EN to "All"
        ),
        "Cash Transactions" to mapOf(
            AppLanguage.RU to "Кассовые операции",
            AppLanguage.TR to "Kasa İşlemleri",
            AppLanguage.EN to "Cash Transactions"
        ),
        "Current Balance" to mapOf(
            AppLanguage.RU to "Текущий баланс",
            AppLanguage.TR to "Mevcut Bakiye",
            AppLanguage.EN to "Current Balance"
        ),
        "Income" to mapOf(
            AppLanguage.RU to "Доходы",
            AppLanguage.TR to "Gelir",
            AppLanguage.EN to "Income"
        ),
        "Expense" to mapOf(
            AppLanguage.RU to "Расходы",
            AppLanguage.TR to "Gider",
            AppLanguage.EN to "Expense"
        ),
        "Recent Transactions" to mapOf(
            AppLanguage.RU to "Последние операции",
            AppLanguage.TR to "Son İşlemler",
            AppLanguage.EN to "Recent Transactions"
        ),
        "New Transaction" to mapOf(
            AppLanguage.RU to "Новая операция",
            AppLanguage.TR to "Yeni İşlem",
            AppLanguage.EN to "New Transaction"
        ),
        "New Cash Transaction" to mapOf(
            AppLanguage.RU to "Новая кассовая операция",
            AppLanguage.TR to "Yeni Kasa Hareketi",
            AppLanguage.EN to "New Cash Transaction"
        ),
        "Add Income" to mapOf(
            AppLanguage.RU to "+ Доход",
            AppLanguage.TR to "+ Gelir Ekle",
            AppLanguage.EN to "Add Income"
        ),
        "Add Expense" to mapOf(
            AppLanguage.RU to "+ Расход",
            AppLanguage.TR to "+ Gider Ekle",
            AppLanguage.EN to "Add Expense"
        ),
        "Expense Category" to mapOf(
            AppLanguage.RU to "Категория расхода",
            AppLanguage.TR to "Gider Kategorisi",
            AppLanguage.EN to "Expense Category"
        ),
        "Salary" to mapOf(
            AppLanguage.RU to "Зарплата",
            AppLanguage.TR to "Maaş",
            AppLanguage.EN to "Salary"
        ),
        "Rent" to mapOf(
            AppLanguage.RU to "Аренда",
            AppLanguage.TR to "Kira",
            AppLanguage.EN to "Rent"
        ),
        "Supplies" to mapOf(
            AppLanguage.RU to "Закупки",
            AppLanguage.TR to "Malzeme / Tedarik",
            AppLanguage.EN to "Supplies"
        ),
        "Utilities" to mapOf(
            AppLanguage.RU to "Коммунальные услуги",
            AppLanguage.TR to "Faturalar",
            AppLanguage.EN to "Utilities"
        ),
        "Other" to mapOf(
            AppLanguage.RU to "Другое",
            AppLanguage.TR to "Diğer",
            AppLanguage.EN to "Other"
        ),
        "Amount" to mapOf(
            AppLanguage.RU to "Сумма",
            AppLanguage.TR to "Tutar",
            AppLanguage.EN to "Amount"
        ),
        "Description / Receipt No" to mapOf(
            AppLanguage.RU to "Описание / № чека",
            AppLanguage.TR to "Açıklama / Fiş No",
            AppLanguage.EN to "Description / Receipt No"
        ),
        "Description" to mapOf(
            AppLanguage.RU to "Описание",
            AppLanguage.TR to "Açıklama",
            AppLanguage.EN to "Description"
        ),
        "Invoices" to mapOf(
            AppLanguage.RU to "Счета",
            AppLanguage.TR to "Cari Hesaplar",
            AppLanguage.EN to "Invoices"
        ),
        "Profit" to mapOf(
            AppLanguage.RU to "Прибыль",
            AppLanguage.TR to "Kâr / Zarar",
            AppLanguage.EN to "Profit"
        ),
        "Profit Report" to mapOf(
            AppLanguage.RU to "Отчет о прибыли",
            AppLanguage.TR to "Kâr / Zarar Raporu",
            AppLanguage.EN to "Profit Report"
        ),
        "Housekeeping Tasks" to mapOf(
            AppLanguage.RU to "Задачи уборки",
            AppLanguage.TR to "Kat Hizmetleri Görevleri",
            AppLanguage.EN to "Housekeeping Tasks"
        ),
        "Dirty" to mapOf(
            AppLanguage.RU to "Грязные",
            AppLanguage.TR to "Kirli",
            AppLanguage.EN to "Dirty"
        ),
        "Clean" to mapOf(
            AppLanguage.RU to "Чистые",
            AppLanguage.TR to "Temiz",
            AppLanguage.EN to "Clean"
        ),
        "Dirty (To be cleaned)" to mapOf(
            AppLanguage.RU to "Грязный (Требуется уборка)",
            AppLanguage.TR to "Kirli (Temizlenecek)",
            AppLanguage.EN to "Dirty (To be cleaned)"
        ),
        "Cleaning in progress" to mapOf(
            AppLanguage.RU to "Идет уборка",
            AppLanguage.TR to "Temizlik Yapılıyor",
            AppLanguage.EN to "Cleaning in progress"
        ),
        "Clean (Inspected)" to mapOf(
            AppLanguage.RU to "Чистый (Проверен)",
            AppLanguage.TR to "Temiz (Kontrol Edildi)",
            AppLanguage.EN to "Clean (Inspected)"
        ),
        "Room Cleaning" to mapOf(
            AppLanguage.RU to "Уборка номера",
            AppLanguage.TR to "Oda Temizliği",
            AppLanguage.EN to "Room Cleaning"
        ),
        "Kat Görevlisi" to mapOf(
            AppLanguage.RU to "Горничная",
            AppLanguage.TR to "Kat Görevlisi",
            AppLanguage.EN to "Housekeeper"
        ),
        "Update Status for Room" to mapOf(
            AppLanguage.RU to "Изменить статус номера",
            AppLanguage.TR to "Oda Durumunu Güncelle:",
            AppLanguage.EN to "Update Status for Room"
        ),
        "Select cleaning status or used supplies for this room." to mapOf(
            AppLanguage.RU to "Выберите статус уборки или использованные расходные материалы.",
            AppLanguage.TR to "Bu oda için temizlik durumunu veya kullanılan sarf malzemeleri seçin.",
            AppLanguage.EN to "Select cleaning status or used supplies for this room."
        ),
        "Cleaned (Select Supplies)" to mapOf(
            AppLanguage.RU to "Убран (Выбор материалов)",
            AppLanguage.TR to "Temizlendi (Malzeme Seç)",
            AppLanguage.EN to "Cleaned (Select Supplies)"
        ),
        "Add Minibar" to mapOf(
            AppLanguage.RU to "Добавить минибар",
            AppLanguage.TR to "Minibar Ekle",
            AppLanguage.EN to "Add Minibar"
        ),
        "Start Cleaning" to mapOf(
            AppLanguage.RU to "Начать уборку",
            AppLanguage.TR to "Temizliğe Başla",
            AppLanguage.EN to "Start Cleaning"
        ),
        "Repairs" to mapOf(
            AppLanguage.RU to "Ремонт",
            AppLanguage.TR to "Bakım",
            AppLanguage.EN to "Repairs"
        ),
        "Used Cleaning Supplies" to mapOf(
            AppLanguage.RU to "Использованные материалы",
            AppLanguage.TR to "Kullanılan Temizlik Malzemeleri",
            AppLanguage.EN to "Used Cleaning Supplies"
        ),
        "Cleaned & Deduct Stock" to mapOf(
            AppLanguage.RU to "Убрано и списать со склада",
            AppLanguage.TR to "Temizlendi & Stoktan Düş",
            AppLanguage.EN to "Cleaned & Deduct Stock"
        ),
        "Select used products and quantities (Will be deducted from stock):" to mapOf(
            AppLanguage.RU to "Выберите использованные материалы и количество (будет списано со склада):",
            AppLanguage.TR to "Kullanılan ürünleri ve miktarları seçiniz (Stoktan düşülecektir):",
            AppLanguage.EN to "Select used products and quantities (Will be deducted from stock):"
        ),
        "Available" to mapOf(
            AppLanguage.RU to "В наличии",
            AppLanguage.TR to "Mevcut",
            AppLanguage.EN to "Available"
        ),
        "No items in stock." to mapOf(
            AppLanguage.RU to "Нет товаров на складе.",
            AppLanguage.TR to "Stokta ürün bulunmuyor.",
            AppLanguage.EN to "No items in stock."
        ),
        "Add Task" to mapOf(
            AppLanguage.RU to "Добавить задачу",
            AppLanguage.TR to "Görev Ekle",
            AppLanguage.EN to "Add Task"
        ),
        "No rooms found to display." to mapOf(
            AppLanguage.RU to "Номера для отображения не найдены.",
            AppLanguage.TR to "Görüntülenecek oda bulunamadı.",
            AppLanguage.EN to "No rooms found to display."
        ),
        "Room Status" to mapOf(
            AppLanguage.RU to "Статус номеров",
            AppLanguage.TR to "Oda Durumları",
            AppLanguage.EN to "Room Status"
        ),
        "Room Summary & Status Stats" to mapOf(
            AppLanguage.RU to "Сводка номеров и статистика",
            AppLanguage.TR to "Oda Özeti ve Durum İstatistikleri",
            AppLanguage.EN to "Room Summary & Status Stats"
        ),
        "GENERAL HOTEL OCCUPANCY STATUS" to mapOf(
            AppLanguage.RU to "ОБЩАЯ ЗАГРУЗКА ОТЕЛЯ",
            AppLanguage.TR to "GENEL OTEL DOLULUK DURUMU",
            AppLanguage.EN to "GENERAL HOTEL OCCUPANCY STATUS"
        ),
        "Total Rooms" to mapOf(
            AppLanguage.RU to "Всего номеров",
            AppLanguage.TR to "Toplam Oda",
            AppLanguage.EN to "Total Rooms"
        ),
        "Room Type Distribution" to mapOf(
            AppLanguage.RU to "Распределение по типам номеров",
            AppLanguage.TR to "Oda Tipi Dağılımı",
            AppLanguage.EN to "Room Type Distribution"
        ),
        "Floor" to mapOf(
            AppLanguage.RU to "Этаж",
            AppLanguage.TR to "Kat",
            AppLanguage.EN to "Floor"
        ),
        "Empty" to mapOf(
            AppLanguage.RU to "Свободен",
            AppLanguage.TR to "Boş",
            AppLanguage.EN to "Empty"
        ),
        "Full" to mapOf(
            AppLanguage.RU to "Занят",
            AppLanguage.TR to "Dolu",
            AppLanguage.EN to "Full"
        ),
        "Reserved" to mapOf(
            AppLanguage.RU to "Бронь",
            AppLanguage.TR to "Rezerve",
            AppLanguage.EN to "Reserved"
        ),
        "Standard" to mapOf(
            AppLanguage.RU to "Стандарт",
            AppLanguage.TR to "Standart",
            AppLanguage.EN to "Standard"
        ),
        "Single" to mapOf(
            AppLanguage.RU to "Одноместный",
            AppLanguage.TR to "Tek Kişilik",
            AppLanguage.EN to "Single"
        ),
        "Double" to mapOf(
            AppLanguage.RU to "Двухместный",
            AppLanguage.TR to "Çift Kişilik",
            AppLanguage.EN to "Double"
        ),
        "Connecting" to mapOf(
            AppLanguage.RU to "Смежный",
            AppLanguage.TR to "Bağlantılı",
            AppLanguage.EN to "Connecting"
        ),
        "Duplex" to mapOf(
            AppLanguage.RU to "Дуплекс",
            AppLanguage.TR to "Dubleks",
            AppLanguage.EN to "Duplex"
        ),
        "Suite" to mapOf(
            AppLanguage.RU to "Люкс",
            AppLanguage.TR to "Süit",
            AppLanguage.EN to "Suite"
        ),
        "Deluxe" to mapOf(
            AppLanguage.RU to "Делюкс",
            AppLanguage.TR to "Delüks",
            AppLanguage.EN to "Deluxe"
        ),
        "King Suite" to mapOf(
            AppLanguage.RU to "Королевский люкс",
            AppLanguage.TR to "Kral Dairesi",
            AppLanguage.EN to "King Suite"
        ),
        "Reservation" to mapOf(
            AppLanguage.RU to "Бронирования",
            AppLanguage.TR to "Rezervasyon",
            AppLanguage.EN to "Reservation"
        ),
        "Room Summary" to mapOf(
            AppLanguage.RU to "Сводка номеров",
            AppLanguage.TR to "Oda Özeti",
            AppLanguage.EN to "Room Summary"
        ),
        "Refresh" to mapOf(
            AppLanguage.RU to "Обновить",
            AppLanguage.TR to "Yenile",
            AppLanguage.EN to "Refresh"
        ),
        "Oda Tiplerine Göre Doluluk" to mapOf(
            AppLanguage.RU to "Заполняемость по типам номеров",
            AppLanguage.TR to "Oda Tiplerine Göre Doluluk",
            AppLanguage.EN to "Occupancy by Room Types"
        ),
        "Oda Tipi" to mapOf(
            AppLanguage.RU to "Тип номера",
            AppLanguage.TR to "Oda Tipi",
            AppLanguage.EN to "Room Type"
        ),
        "Search guest name, date or room type..." to mapOf(
            AppLanguage.RU to "Поиск по имени, дате или типу...",
            AppLanguage.TR to "Misafir Adı, Tarih veya Oda Tipi Ara...",
            AppLanguage.EN to "Search guest name, date or room type..."
        ),
        "Bakım & Onarım (Arızalar)" to mapOf(
            AppLanguage.RU to "Техобслуживание и ремонт",
            AppLanguage.TR to "Bakım & Onarım (Arızalar)",
            AppLanguage.EN to "Maintenance & Repairs"
        ),
        "Yeni Arıza Bildir" to mapOf(
            AppLanguage.RU to "Новая заявка на ремонт",
            AppLanguage.TR to "Yeni Arıza Bildir",
            AppLanguage.EN to "Report New Issue"
        ),
        "Oda Seçin" to mapOf(
            AppLanguage.RU to "Выберите номер",
            AppLanguage.TR to "Oda Seçin",
            AppLanguage.EN to "Select Room"
        ),
        "Kategori" to mapOf(
            AppLanguage.RU to "Категория",
            AppLanguage.TR to "Kategori",
            AppLanguage.EN to "Category"
        ),
        "Genel" to mapOf(
            AppLanguage.RU to "Общее",
            AppLanguage.TR to "Genel",
            AppLanguage.EN to "General"
        ),
        "Klima" to mapOf(
            AppLanguage.RU to "Кондиционер",
            AppLanguage.TR to "Klima",
            AppLanguage.EN to "Air Conditioning"
        ),
        "Tesisat" to mapOf(
            AppLanguage.RU to "Сантехника",
            AppLanguage.TR to "Tesisat",
            AppLanguage.EN to "Plumbing"
        ),
        "Elektrik" to mapOf(
            AppLanguage.RU to "Электрика",
            AppLanguage.TR to "Elektrik",
            AppLanguage.EN to "Electrical"
        ),
        "Mobilya" to mapOf(
            AppLanguage.RU to "Мебель",
            AppLanguage.TR to "Mobilya",
            AppLanguage.EN to "Furniture"
        ),
        "TV/İnternet" to mapOf(
            AppLanguage.RU to "ТВ / Интернет",
            AppLanguage.TR to "TV/İnternet",
            AppLanguage.EN to "TV / Internet"
        ),
        "Öncelik" to mapOf(
            AppLanguage.RU to "Приоритет",
            AppLanguage.TR to "Öncelik",
            AppLanguage.EN to "Priority"
        ),
        "DÜŞÜK" to mapOf(
            AppLanguage.RU to "НИЗКИЙ",
            AppLanguage.TR to "DÜŞÜK",
            AppLanguage.EN to "LOW"
        ),
        "NORMAL" to mapOf(
            AppLanguage.RU to "ОБЫЧНЫЙ",
            AppLanguage.TR to "NORMAL",
            AppLanguage.EN to "NORMAL"
        ),
        "ACİL" to mapOf(
            AppLanguage.RU to "СРОЧНО",
            AppLanguage.TR to "ACİL",
            AppLanguage.EN to "URGENT"
        ),
        "Arıza Açıklaması" to mapOf(
            AppLanguage.RU to "Описание неисправности",
            AppLanguage.TR to "Arıza Açıklaması",
            AppLanguage.EN to "Issue Description"
        ),
        "Arızalar" to mapOf(
            AppLanguage.RU to "Неисправности",
            AppLanguage.TR to "Arızalar",
            AppLanguage.EN to "Issues"
        ),
        "Çözülenler" to mapOf(
            AppLanguage.RU to "Решенные",
            AppLanguage.TR to "Çözülenler",
            AppLanguage.EN to "Resolved"
        ),
        "Parçalar" to mapOf(
            AppLanguage.RU to "Запчасти / Склад",
            AppLanguage.TR to "Parçalar",
            AppLanguage.EN to "Parts / Stock"
        ),
        "Aktif arıza kaydı bulunmuyor." to mapOf(
            AppLanguage.RU to "Нет активных заявок на ремонт.",
            AppLanguage.TR to "Aktif arıza kaydı bulunmuyor.",
            AppLanguage.EN to "No active maintenance records."
        ),
        "Ad veya Kimlik No ile ara..." to mapOf(
            AppLanguage.RU to "Поиск по имени или паспорту...",
            AppLanguage.TR to "Ad veya Kimlik No ile ara...",
            AppLanguage.EN to "Search by name or passport..."
        ),
        "Kara Liste Ekle" to mapOf(
            AppLanguage.RU to "+ В черный список",
            AppLanguage.TR to "+ Kara Liste Ekle",
            AppLanguage.EN to "+ Add to Blacklist"
        ),
        "Yeni Kara Liste Kaydı" to mapOf(
            AppLanguage.RU to "Новая запись в черном списке",
            AppLanguage.TR to "Yeni Kara Liste Kaydı",
            AppLanguage.EN to "New Blacklist Record"
        ),
        "Müşteri Adı Soyadı *" to mapOf(
            AppLanguage.RU to "ФИО гостя *",
            AppLanguage.TR to "Müşteri Adı Soyadı *",
            AppLanguage.EN to "Guest Full Name *"
        ),
        "TC Kimlik / Pasaport No *" to mapOf(
            AppLanguage.RU to "Номер паспорта / документа *",
            AppLanguage.TR to "TC Kimlik / Pasaport No *",
            AppLanguage.EN to "Passport / ID No *"
        ),
        "TC / Pasaport No" to mapOf(
            AppLanguage.RU to "Паспорт / Документ",
            AppLanguage.TR to "TC / Pasaport No",
            AppLanguage.EN to "Passport / ID No"
        ),
        "Kimlik No Girilmemiş" to mapOf(
            AppLanguage.RU to "Документ не указан",
            AppLanguage.TR to "Kimlik No Girilmemiş",
            AppLanguage.EN to "ID No Not Entered"
        ),
        "Kara Listeye Alınma Gerekçesi *" to mapOf(
            AppLanguage.RU to "Причина внесения в черный список *",
            AppLanguage.TR to "Kara Listeye Alınma Gerekçesi *",
            AppLanguage.EN to "Reason for Blacklisting *"
        ),
        "Örn: Ödeme yapmadan kaçış, tesise zarar verme vb." to mapOf(
            AppLanguage.RU to "Напр: Неоплата, порча имущества и т.д.",
            AppLanguage.TR to "Örn: Ödeme yapmadan kaçış, tesise zarar verme vb.",
            AppLanguage.EN to "e.g. Non-payment, damage to property, etc."
        ),
        "Kaydet ve Engelle" to mapOf(
            AppLanguage.RU to "Сохранить и заблокировать",
            AppLanguage.TR to "Kaydet ve Engelle",
            AppLanguage.EN to "Save & Block"
        ),
        "Kara Liste Kaydını Sil" to mapOf(
            AppLanguage.RU to "Удалить из черного списка",
            AppLanguage.TR to "Kara Liste Kaydını Sil",
            AppLanguage.EN to "Delete Blacklist Record"
        ),
        "isimli misafiri kara listeden çıkarmak istediğinize emin misiniz?" to mapOf(
            AppLanguage.RU to "Вы уверены, что хотите удалить этого гостя из черного списка?",
            AppLanguage.TR to "isimli misafiri kara listeden çıkarmak istediğinize emin misiniz?",
            AppLanguage.EN to "Are you sure you want to remove this guest from the blacklist?"
        ),
        "Evet, Çıkar" to mapOf(
            AppLanguage.RU to "Да, удалить",
            AppLanguage.TR to "Evet, Çıkar",
            AppLanguage.EN to "Yes, Remove"
        ),
        "Gerekçe / Nedeni:" to mapOf(
            AppLanguage.RU to "Причина:",
            AppLanguage.TR to "Gerekçe / Nedeni:",
            AppLanguage.EN to "Reason:"
        ),
        "Gerekçe belirtilmemiş." to mapOf(
            AppLanguage.RU to "Причина не указана.",
            AppLanguage.TR to "Gerekçe belirtilmemiş.",
            AppLanguage.EN to "No reason specified."
        ),
        "Arama kriterine uygun kara liste kaydı bulunamadı." to mapOf(
            AppLanguage.RU to "Записей в черном списке по вашему запросу не найдено.",
            AppLanguage.TR to "Arama kriterine uygun kara liste kaydı bulunamadı.",
            AppLanguage.EN to "No matching blacklist record found."
        ),
        "Henüz kara listeye alınmış müşteri bulunmuyor." to mapOf(
            AppLanguage.RU to "В черном списке пока нет гостей.",
            AppLanguage.TR to "Henüz kara listeye alınmış müşteri bulunmuyor.",
            AppLanguage.EN to "No blacklisted guests yet."
        ),
        "1. Acente Seçimi" to mapOf(
            AppLanguage.RU to "1. Выбор агентства",
            AppLanguage.TR to "1. Acente Seçimi",
            AppLanguage.EN to "1. Agency Selection"
        ),
        "Sistemde kayıtlı acente yok" to mapOf(
            AppLanguage.RU to "В системе нет зарегистрированных агентств",
            AppLanguage.TR to "Sistemde kayıtlı acente yok",
            AppLanguage.EN to "No agencies registered in the system"
        ),
        "Acente Seçiniz..." to mapOf(
            AppLanguage.RU to "Выберите агентство...",
            AppLanguage.TR to "Acente Seçiniz...",
            AppLanguage.EN to "Select Agency..."
        ),
        "Seç" to mapOf(
            AppLanguage.RU to "Выбрать",
            AppLanguage.TR to "Seç",
            AppLanguage.EN to "Select"
        ),
        "2. Sözleşme Şartları ve Kontenjan" to mapOf(
            AppLanguage.RU to "2. Условия договора и квоты",
            AppLanguage.TR to "2. Sözleşme Şartları ve Kontenjan",
            AppLanguage.EN to "2. Contract Terms and Allotment"
        ),
        "Başlangıç Tarihi" to mapOf(
            AppLanguage.RU to "Дата начала",
            AppLanguage.TR to "Başlangıç Tarihi",
            AppLanguage.EN to "Start Date"
        ),
        "Bitiş Tarihi" to mapOf(
            AppLanguage.RU to "Дата окончания",
            AppLanguage.TR to "Bitiş Tarihi",
            AppLanguage.EN to "End Date"
        ),
        "Oda Kontenjanı" to mapOf(
            AppLanguage.RU to "Квота номеров",
            AppLanguage.TR to "Oda Kontenjanı",
            AppLanguage.EN to "Room Allotment"
        ),
        "İndirim/Komisyon (%)" to mapOf(
            AppLanguage.RU to "Скидка/Комиссия (%)",
            AppLanguage.TR to "İndirim/Komisyon (%)",
            AppLanguage.EN to "Discount/Commission (%)"
        ),
        "Pansiyon Tipi" to mapOf(
            AppLanguage.RU to "Тип питания",
            AppLanguage.TR to "Pansiyon Tipi",
            AppLanguage.EN to "Board Type"
        ),
        "Release (Gün)" to mapOf(
            AppLanguage.RU to "Релиз-период (дней)",
            AppLanguage.TR to "Release (Gün)",
            AppLanguage.EN to "Release (Days)"
        ),
        "Stop-Sale İhbar (Gün)" to mapOf(
            AppLanguage.RU to "Срок уведомления Stop-Sale (дней)",
            AppLanguage.TR to "Stop-Sale İhbar (Gün)",
            AppLanguage.EN to "Stop-Sale Notice (Days)"
        ),
        "Ödeme Şekli / Koşulu" to mapOf(
            AppLanguage.RU to "Условия и форма оплаты",
            AppLanguage.TR to "Ödeme Şekli / Koşulu",
            AppLanguage.EN to "Payment Term / Condition"
        ),
        "3. Özel Hükümler (Serbest Metin)" to mapOf(
            AppLanguage.RU to "3. Особые условия (свободный текст)",
            AppLanguage.TR to "3. Özel Hükümler (Serbest Metin)",
            AppLanguage.EN to "3. Special Terms (Free Text)"
        ),
        "Özel şartları veya ilave kuralları buraya yazabilirsiniz..." to mapOf(
            AppLanguage.RU to "Вы можете указать особые условия или правила здесь...",
            AppLanguage.TR to "Özel şartları veya ilave kuralları buraya yazabilirsiniz...",
            AppLanguage.EN to "You can write special conditions or rules here..."
        ),
        "Sözleşme Dokümanı Önizleme" to mapOf(
            AppLanguage.RU to "Предпросмотр документа договора",
            AppLanguage.TR to "Sözleşme Dokümanı Önizleme",
            AppLanguage.EN to "Contract Document Preview"
        ),
        "RESMİ SÖZLEŞME METNİ" to mapOf(
            AppLanguage.RU to "ОФИЦИАЛЬНЫЙ ТЕКСТ ДОГОВОРА",
            AppLanguage.TR to "RESMİ SÖZLEŞME METNİ",
            AppLanguage.EN to "OFFICIAL CONTRACT TEXT"
        ),
        "Metni Kopyala" to mapOf(
            AppLanguage.RU to "Копировать текст",
            AppLanguage.TR to "Metni Kopyala",
            AppLanguage.EN to "Copy Text"
        ),
        "Hazırla / Yazdır" to mapOf(
            AppLanguage.RU to "Сформировать / Печать",
            AppLanguage.TR to "Hazırla / Yazdır",
            AppLanguage.EN to "Generate / Print"
        ),
        "Sözleşme metni panoya kopyalandı!" to mapOf(
            AppLanguage.RU to "Текст договора скопирован в буфер обмена!",
            AppLanguage.TR to "Sözleşme metni panoya kopyalandı!",
            AppLanguage.EN to "Contract text copied to clipboard!"
        ),
        "Otel Profili" to mapOf(
            AppLanguage.RU to "Профиль отеля",
            AppLanguage.TR to "Otel Profili",
            AppLanguage.EN to "Hotel Profile"
        ),
        "Otel Ticari Adı" to mapOf(
            AppLanguage.RU to "Коммерческое название отеля",
            AppLanguage.TR to "Otel Ticari Adı",
            AppLanguage.EN to "Hotel Commercial Name"
        ),
        "Şirket / Otel Resmi Ünvanı" to mapOf(
            AppLanguage.RU to "Юридическое название отеля",
            AppLanguage.TR to "Şirket / Otel Resmi Ünvanı",
            AppLanguage.EN to "Official Company Title"
        ),
        "Telefon Numarası" to mapOf(
            AppLanguage.RU to "Номер телефона",
            AppLanguage.TR to "Telefon Numarası",
            AppLanguage.EN to "Phone Number"
        ),
        "E-posta Adresi" to mapOf(
            AppLanguage.RU to "Адрес эл. почты",
            AppLanguage.TR to "E-posta Adresi",
            AppLanguage.EN to "Email Address"
        ),
        "Açık Adres" to mapOf(
            AppLanguage.RU to "Фактический адрес",
            AppLanguage.TR to "Açık Adres",
            AppLanguage.EN to "Full Address"
        ),
        "Vergi Dairesi" to mapOf(
            AppLanguage.RU to "Налоговая инспекция",
            AppLanguage.TR to "Vergi Dairesi",
            AppLanguage.EN to "Tax Office"
        ),
        "Vergi Numarası" to mapOf(
            AppLanguage.RU to "ИНН / Налоговый номер",
            AppLanguage.TR to "Vergi Numarası",
            AppLanguage.EN to "Tax / TIN Number"
        ),
        "Otel Yetkilisi (Adı Soyadı / Unvanı)" to mapOf(
            AppLanguage.RU to "Представитель отеля (ФИО / Должность)",
            AppLanguage.TR to "Otel Yetkilisi (Adı Soyadı / Unvanı)",
            AppLanguage.EN to "Hotel Representative (Full Name / Title)"
        ),
        "Sosyal Medya Linki (Örn: Instagram)" to mapOf(
            AppLanguage.RU to "Ссылка на соцсети (напр. Instagram)",
            AppLanguage.TR to "Sosyal Medya Linki (Örn: Instagram)",
            AppLanguage.EN to "Social Media Link (e.g. Instagram)"
        ),
        "Yeni Oda Ekle" to mapOf(
            AppLanguage.RU to "Добавить новый номер",
            AppLanguage.TR to "Yeni Oda Ekle",
            AppLanguage.EN to "Add New Room"
        ),
        "Oda Tipi Seçin" to mapOf(
            AppLanguage.RU to "Выберите тип номера",
            AppLanguage.TR to "Oda Tipi Seçin",
            AppLanguage.EN to "Select Room Type"
        ),
        "Oda Numarası (Örn: 101)" to mapOf(
            AppLanguage.RU to "Номер (напр. 101)",
            AppLanguage.TR to "Oda Numarası (Örn: 101)",
            AppLanguage.EN to "Room Number (e.g. 101)"
        ),
        "Oda Ekle" to mapOf(
            AppLanguage.RU to "Добавить номер",
            AppLanguage.TR to "Oda Ekle",
            AppLanguage.EN to "Add Room"
        ),
        "Oda Filtrele" to mapOf(
            AppLanguage.RU to "Фильтр номеров",
            AppLanguage.TR to "Oda Filtrele",
            AppLanguage.EN to "Filter Rooms"
        ),
        "Odayı Düzenle veya Sil" to mapOf(
            AppLanguage.RU to "Редактировать или удалить номер",
            AppLanguage.TR to "Odayı Düzenle veya Sil",
            AppLanguage.EN to "Edit or Delete Room"
        ),
        "Oda Durumu" to mapOf(
            AppLanguage.RU to "Статус номера",
            AppLanguage.TR to "Oda Durumu",
            AppLanguage.EN to "Room Status"
        ),
        "Oda Numarası" to mapOf(
            AppLanguage.RU to "Номер",
            AppLanguage.TR to "Oda Numarası",
            AppLanguage.EN to "Room Number"
        ),
        "Kayıtlı Acentalar" to mapOf(
            AppLanguage.RU to "Зарегистрированные агентства",
            AppLanguage.TR to "Kayıtlı Acentalar",
            AppLanguage.EN to "Registered Agencies"
        ),
        "+ Sözleşme" to mapOf(
            AppLanguage.RU to "+ Договор",
            AppLanguage.TR to "+ Sözleşme",
            AppLanguage.EN to "+ Contract"
        ),
        "+ Kontenjan" to mapOf(
            AppLanguage.RU to "+ Квота",
            AppLanguage.TR to "+ Kontenjan",
            AppLanguage.EN to "+ Allotment"
        ),
        "+ Acenta" to mapOf(
            AppLanguage.RU to "+ Агентство",
            AppLanguage.TR to "+ Acenta",
            AppLanguage.EN to "+ Agency"
        ),
        "Komisyon" to mapOf(
            AppLanguage.RU to "Комиссия",
            AppLanguage.TR to "Komisyon",
            AppLanguage.EN to "Commission"
        ),
        "Acenta Düzenle" to mapOf(
            AppLanguage.RU to "Редактировать агентство",
            AppLanguage.TR to "Acenta Düzenle",
            AppLanguage.EN to "Edit Agency"
        ),
        "Acenta Ekle" to mapOf(
            AppLanguage.RU to "Добавить агентство",
            AppLanguage.TR to "Acenta Ekle",
            AppLanguage.EN to "Add Agency"
        ),
        "Acenta Adı" to mapOf(
            AppLanguage.RU to "Название агентства",
            AppLanguage.TR to "Acenta Adı",
            AppLanguage.EN to "Agency Name"
        ),
        "İletişim Bilgisi" to mapOf(
            AppLanguage.RU to "Контактная информация",
            AppLanguage.TR to "İletişim Bilgisi",
            AppLanguage.EN to "Contact Info"
        ),
        "Toplu Periyot Fiyatı Ekle" to mapOf(
            AppLanguage.RU to "Массовое добавление цен по периодам",
            AppLanguage.TR to "Toplu Periyot Fiyatı Ekle",
            AppLanguage.EN to "Bulk Add Seasonal Prices"
        ),
        "Periyot Adı (Örn: Yaz Sezonu)" to mapOf(
            AppLanguage.RU to "Название периода (напр. Летний сезон)",
            AppLanguage.TR to "Periyot Adı (Örn: Yaz Sezonu)",
            AppLanguage.EN to "Period Name (e.g. Summer Season)"
        ),
        "Başlangıç" to mapOf(
            AppLanguage.RU to "Начало",
            AppLanguage.TR to "Başlangıç",
            AppLanguage.EN to "Start"
        ),
        "Bitiş" to mapOf(
            AppLanguage.RU to "Окончание",
            AppLanguage.TR to "Bitiş",
            AppLanguage.EN to "End"
        ),
        "Oda Tipleri ve Fiyatları (₽)" to mapOf(
            AppLanguage.RU to "Категории номеров и цены (₽)",
            AppLanguage.TR to "Oda Tipleri ve Fiyatları (₽)",
            AppLanguage.EN to "Room Types and Rates (₽)"
        ),
        "Menü Yönetimi" to mapOf(
            AppLanguage.RU to "Управление меню",
            AppLanguage.TR to "Menü Yönetimi",
            AppLanguage.EN to "Menu Management"
        ),
        "Ürün Kayıtlı" to mapOf(
            AppLanguage.RU to "товаров в меню",
            AppLanguage.TR to "Ürün Kayıtlı",
            AppLanguage.EN to "Items Registered"
        ),
        "+ Yeni Ürün Ekle" to mapOf(
            AppLanguage.RU to "+ Добавить позицию",
            AppLanguage.TR to "+ Yeni Ürün Ekle",
            AppLanguage.EN to "+ Add New Item"
        ),
        "Menüde yemek, içecek ara..." to mapOf(
            AppLanguage.RU to "Поиск по меню...",
            AppLanguage.TR to "Menüde yemek, içecek ara...",
            AppLanguage.EN to "Search menu items..."
        ),
        "Kategoriler" to mapOf(
            AppLanguage.RU to "Категории",
            AppLanguage.TR to "Kategoriler",
            AppLanguage.EN to "Categories"
        ),
        "All Items" to mapOf(
            AppLanguage.RU to "Все позиции",
            AppLanguage.TR to "Tüm Ürünler",
            AppLanguage.EN to "All Items"
        ),
        "Desserts" to mapOf(
            AppLanguage.RU to "Десерты",
            AppLanguage.TR to "Tatlılar",
            AppLanguage.EN to "Desserts"
        ),
        "Alcohol" to mapOf(
            AppLanguage.RU to "Алкоголь",
            AppLanguage.TR to "Alkollü İçecekler",
            AppLanguage.EN to "Alcohol"
        ),
        "Temizle" to mapOf(
            AppLanguage.RU to "Очистить",
            AppLanguage.TR to "Temizle",
            AppLanguage.EN to "Clear"
        ),
        "Departman Ekle" to mapOf(
            AppLanguage.RU to "Добавить отдел",
            AppLanguage.TR to "Departman Ekle",
            AppLanguage.EN to "Add Department"
        ),
        "Henüz tanımlı departman bulunmuyor." to mapOf(
            AppLanguage.RU to "В системе нет созданных отделов.",
            AppLanguage.TR to "Henüz tanımlı departman bulunmuyor.",
            AppLanguage.EN to "No departments defined yet."
        ),
        "Yeni Departman Ekle" to mapOf(
            AppLanguage.RU to "Добавить новый отдел",
            AppLanguage.TR to "Yeni Departman Ekle",
            AppLanguage.EN to "Add New Department"
        ),
        "Departmanı Düzenle" to mapOf(
            AppLanguage.RU to "Редактировать отдел",
            AppLanguage.TR to "Departmanı Düzenle",
            AppLanguage.EN to "Edit Department"
        ),
        "Departman Adı" to mapOf(
            AppLanguage.RU to "Название отдела",
            AppLanguage.TR to "Departman Adı",
            AppLanguage.EN to "Department Name"
        ),
        "Yetkiler (Görebileceği Sayfalar)" to mapOf(
            AppLanguage.RU to "Права доступа (доступные страницы)",
            AppLanguage.TR to "Yetkiler (Görebileceği Sayfalar)",
            AppLanguage.EN to "Permissions (Accessible Pages)"
        ),
        "yetki tanımlı" to mapOf(
            AppLanguage.RU to "прав назначено",
            AppLanguage.TR to "yetki tanımlı",
            AppLanguage.EN to "permissions assigned"
        ),
        "AI Asistan" to mapOf(
            AppLanguage.RU to "AI Ассистент",
            AppLanguage.TR to "AI Asistan",
            AppLanguage.EN to "AI Assistant"
        ),
        "CRM & Misafir" to mapOf(
            AppLanguage.RU to "CRM и Гости",
            AppLanguage.TR to "CRM & Misafir",
            AppLanguage.EN to "CRM & Guests"
        ),
        "Kanal Yönetimi" to mapOf(
            AppLanguage.RU to "Менеджер каналов",
            AppLanguage.TR to "Kanal Yönetimi",
            AppLanguage.EN to "Channel Manager"
        ),
        "Dinamik Fiyatlandırma" to mapOf(
            AppLanguage.RU to "Динамическое ценообразование",
            AppLanguage.TR to "Dinamik Fiyatlandırma",
            AppLanguage.EN to "Dynamic Pricing"
        ),
        "Rezervasyon Takvimi" to mapOf(
            AppLanguage.RU to "Календарь бронирований",
            AppLanguage.TR to "Rezervasyon Takvimi",
            AppLanguage.EN to "Reservation Calendar"
        ),
        "Yeni Rezervasyon" to mapOf(
            AppLanguage.RU to "Новое бронирование",
            AppLanguage.TR to "Yeni Rezervasyon",
            AppLanguage.EN to "New Reservation"
        ),
        "Check-in Listesi" to mapOf(
            AppLanguage.RU to "Список заездов",
            AppLanguage.TR to "Check-in Listesi",
            AppLanguage.EN to "Check-in List"
        ),
        "Check-out Listesi" to mapOf(
            AppLanguage.RU to "Список выездов",
            AppLanguage.TR to "Check-out Listesi",
            AppLanguage.EN to "Check-out List"
        ),
        "Gelir/Gider Raporu" to mapOf(
            AppLanguage.RU to "Отчет о доходах и расходах",
            AppLanguage.TR to "Gelir/Gider Raporu",
            AppLanguage.EN to "Income / Expense Report"
        ),
        "İstatistikler" to mapOf(
            AppLanguage.RU to "Статистика",
            AppLanguage.TR to "İstatistikler",
            AppLanguage.EN to "Statistics"
        ),
        "Kullanımdaki Odalar" to mapOf(
            AppLanguage.RU to "Занятые номера",
            AppLanguage.TR to "Kullanımdaki Odalar",
            AppLanguage.EN to "Occupied Rooms"
        ),
        "Bakım & Arıza" to mapOf(
            AppLanguage.RU to "Техобслуживание и ремонт",
            AppLanguage.TR to "Bakım & Arıza",
            AppLanguage.EN to "Maintenance & Repairs"
        ),
        "Kasa" to mapOf(
            AppLanguage.RU to "Касса / Финансы",
            AppLanguage.TR to "Kasa",
            AppLanguage.EN to "Cash Register"
        ),
        "Stok / Envanter" to mapOf(
            AppLanguage.RU to "Склад / Инвентарь",
            AppLanguage.TR to "Stok / Envanter",
            AppLanguage.EN to "Stock / Inventory"
        ),
        "Vardiya Planlama" to mapOf(
            AppLanguage.RU to "График смен",
            AppLanguage.TR to "Vardiya Planlama",
            AppLanguage.EN to "Shift Planning"
        ),
        "Personel Yönetimi" to mapOf(
            AppLanguage.RU to "Управление персоналом",
            AppLanguage.TR to "Personel Yönetimi",
            AppLanguage.EN to "Staff Management"
        ),
        "Sistem Yönetimi" to mapOf(
            AppLanguage.RU to "Администрирование",
            AppLanguage.TR to "Sistem Yönetimi",
            AppLanguage.EN to "System Administration"
        ),
        "Yeni Personel" to mapOf(
            AppLanguage.RU to "Новый сотрудник",
            AppLanguage.TR to "Yeni Personel",
            AppLanguage.EN to "New Staff"
        ),
        "Yeni Personel Ekle" to mapOf(
            AppLanguage.RU to "Добавить нового сотрудника",
            AppLanguage.TR to "Yeni Personel Ekle",
            AppLanguage.EN to "Add New Staff"
        ),
        "Henüz sisteme kayıtlı personel bulunmuyor." to mapOf(
            AppLanguage.RU to "В системе нет зарегистрированных сотрудников.",
            AppLanguage.TR to "Henüz sisteme kayıtlı personel bulunmuyor.",
            AppLanguage.EN to "No staff registered in the system yet."
        ),
        "Rol belirtilmedi" to mapOf(
            AppLanguage.RU to "Роль не назначена",
            AppLanguage.TR to "Rol belirtilmedi",
            AppLanguage.EN to "No role specified"
        ),
        "Roller" to mapOf(
            AppLanguage.RU to "Роли",
            AppLanguage.TR to "Roller",
            AppLanguage.EN to "Roles"
        ),
        "FRONT_DESK" to mapOf(
            AppLanguage.RU to "Ресепшн / Служба приема",
            AppLanguage.TR to "Resepsiyon / Ön Büro",
            AppLanguage.EN to "Front Desk / Reception"
        ),
        "HOUSEKEEPING" to mapOf(
            AppLanguage.RU to "Хаускипинг / Уборка",
            AppLanguage.TR to "Kat Hizmetleri",
            AppLanguage.EN to "Housekeeping"
        ),
        "RESTAURANT" to mapOf(
            AppLanguage.RU to "Ресторан / POS",
            AppLanguage.TR to "Restoran",
            AppLanguage.EN to "Restaurant"
        ),
        "ACCOUNTING" to mapOf(
            AppLanguage.RU to "Бухгалтерия / Финансы",
            AppLanguage.TR to "Muhasebe",
            AppLanguage.EN to "Accounting"
        ),
        "MANAGER" to mapOf(
            AppLanguage.RU to "Управляющий / Менеджер",
            AppLanguage.TR to "Yönetici",
            AppLanguage.EN to "Manager"
        ),
        "Ekle" to mapOf(
            AppLanguage.RU to "Добавить",
            AppLanguage.TR to "Ekle",
            AppLanguage.EN to "Add"
        ),
        "Yeni Oda" to mapOf(
            AppLanguage.RU to "Новый номер",
            AppLanguage.TR to "Yeni Oda",
            AppLanguage.EN to "New Room"
        ),
        "Oda Tanımlı" to mapOf(
            AppLanguage.RU to "Номеров добавлено",
            AppLanguage.TR to "Oda Tanımlı",
            AppLanguage.EN to "Rooms Defined"
        ),
        "Odayı Düzenle" to mapOf(
            AppLanguage.RU to "Редактировать номер",
            AppLanguage.TR to "Odayı Düzenle",
            AppLanguage.EN to "Edit Room"
        ),
        "Lütfen oda numarası giriniz." to mapOf(
            AppLanguage.RU to "Пожалуйста, введите номер.",
            AppLanguage.TR to "Lütfen oda numarası giriniz.",
            AppLanguage.EN to "Please enter room number."
        ),
        "Odayı Sil" to mapOf(
            AppLanguage.RU to "Удалить номер",
            AppLanguage.TR to "Odayı Sil",
            AppLanguage.EN to "Delete Room"
        ),
        "Bu odayı silmek istediğinize emin misiniz?" to mapOf(
            AppLanguage.RU to "Вы уверены, что хотите удалить этот номер?",
            AppLanguage.TR to "Bu odayı silmek istediğinize emin misiniz?",
            AppLanguage.EN to "Are you sure you want to delete this room?"
        ),
        "Dönem Tanımlı" to mapOf(
            AppLanguage.RU to "Периодов настроено",
            AppLanguage.TR to "Dönem Tanımlı",
            AppLanguage.EN to "Periods Defined"
        ),
        "Yeni Periyot Ekle" to mapOf(
            AppLanguage.RU to "Добавить период",
            AppLanguage.TR to "Yeni Periyot Ekle",
            AppLanguage.EN to "Add Period"
        ),
        "Yeni Fiyat Periyodu Ekle" to mapOf(
            AppLanguage.RU to "Добавить ценовой период",
            AppLanguage.TR to "Yeni Fiyat Periyodu Ekle",
            AppLanguage.EN to "Add Price Period"
        ),
        "Periyot Adı" to mapOf(
            AppLanguage.RU to "Название периода",
            AppLanguage.TR to "Periyot Adı",
            AppLanguage.EN to "Period Name"
        ),
        "Oda Tipi Fiyatları (₽)" to mapOf(
            AppLanguage.RU to "Цены категорий номеров (₽)",
            AppLanguage.TR to "Oda Tipi Fiyatları (₽)",
            AppLanguage.EN to "Room Type Rates (₽)"
        ),
        "Oda Tipi Fiyatları" to mapOf(
            AppLanguage.RU to "Цены по типам номеров",
            AppLanguage.TR to "Oda Tipi Fiyatları",
            AppLanguage.EN to "Room Type Prices"
        ),
        "Lütfen periyot adı, tarihler ve en az bir oda fiyatı giriniz." to mapOf(
            AppLanguage.RU to "Пожалуйста, введите название периода, даты и хотя бы одну цену.",
            AppLanguage.TR to "Lütfen periyot adı, tarihler ve en az bir oda fiyatı giriniz.",
            AppLanguage.EN to "Please enter period name, dates and at least one room price."
        ),
        "Periyodu Sil" to mapOf(
            AppLanguage.RU to "Удалить период",
            AppLanguage.TR to "Periyodu Sil",
            AppLanguage.EN to "Delete Period"
        ),
        "Bu periyottaki tüm oda fiyatları kalıcı olarak silinecek. Onaylıyor musunuz?" to mapOf(
            AppLanguage.RU to "Все цены на номера в этом периоде будут безвозвратно удалены. Подтверждаете?",
            AppLanguage.TR to "Bu periyottaki tüm oda fiyatları kalıcı olarak silinecek. Onaylıyor musunuz?",
            AppLanguage.EN to "All room prices in this period will be permanently deleted. Confirm?"
        ),
        "Toplu Sil" to mapOf(
            AppLanguage.RU to "Удалить все",
            AppLanguage.TR to "Toplu Sil",
            AppLanguage.EN to "Bulk Delete"
        ),
        "Fiyatını Düzenle" to mapOf(
            AppLanguage.RU to "Редактировать цену",
            AppLanguage.TR to "Fiyatını Düzenle",
            AppLanguage.EN to "Edit Price"
        ),
        "Güncelle" to mapOf(
            AppLanguage.RU to "Обновить",
            AppLanguage.TR to "Güncelle",
            AppLanguage.EN to "Update"
        ),
        "Sistem Yönetim Paneli" to mapOf(
            AppLanguage.RU to "Панель управления системой",
            AppLanguage.TR to "Sistem Yönetim Paneli",
            AppLanguage.EN to "System Management Panel"
        ),
        "Otel ve Sistem Yapılandırması" to mapOf(
            AppLanguage.RU to "Конфигурация отеля и системы",
            AppLanguage.TR to "Otel ve Sistem Yapılandırması",
            AppLanguage.EN to "Hotel & System Configuration"
        ),
        "Otel & Konaklama Yapılandırması" to mapOf(
            AppLanguage.RU to "Конфигурация отеля и проживания",
            AppLanguage.TR to "Otel & Konaklama Yapılandırması",
            AppLanguage.EN to "Hotel & Accommodation Configuration"
        ),
        "Otel bilgileri, oda tipleri, envanter ve fiyat tarifeleri" to mapOf(
            AppLanguage.RU to "Информация об отеле, типы номеров, инвентарь и тарифы",
            AppLanguage.TR to "Otel bilgileri, oda tipleri, envanter ve fiyat tarifeleri",
            AppLanguage.EN to "Hotel info, room types, inventory and pricing"
        ),
        "Kurumsal unvan, iletişim, vergi ve fatura bilgileri" to mapOf(
            AppLanguage.RU to "Юридическое наименование, контакты, налоговые и платежные реквизиты",
            AppLanguage.TR to "Kurumsal unvan, iletişim, vergi ve fatura bilgileri",
            AppLanguage.EN to "Company title, contact, tax and billing information"
        ),
        "Oda Yönetimi" to mapOf(
            AppLanguage.RU to "Управление номерами",
            AppLanguage.TR to "Oda Yönetimi",
            AppLanguage.EN to "Room Management"
        ),
        "Oda numaraları, kat tanımları ve oda tipleri" to mapOf(
            AppLanguage.RU to "Номера, этажи и категории номеров",
            AppLanguage.TR to "Oda numaraları, kat tanımları ve oda tipleri",
            AppLanguage.EN to "Room numbers, floor definitions and room types"
        ),
        "Fiyat Periyotları & Tarifeler" to mapOf(
            AppLanguage.RU to "Ценовые периоды и тарифы",
            AppLanguage.TR to "Fiyat Periyotları & Tarifeler",
            AppLanguage.EN to "Pricing Periods & Rates"
        ),
        "Sezonluk fiyatlar, hafta sonu farkları ve oda tarifeleri" to mapOf(
            AppLanguage.RU to "Сезонные цены, надбавки выходного дня и тарифы на номера",
            AppLanguage.TR to "Sezonluk fiyatlar, hafta sonu farkları ve oda tarifeleri",
            AppLanguage.EN to "Seasonal rates, weekend surcharges and room rates"
        ),
        "Satış, Menü & İş Ortakları" to mapOf(
            AppLanguage.RU to "Продажи, меню и партнеры",
            AppLanguage.TR to "Satış, Menü & İş Ortakları",
            AppLanguage.EN to "Sales, Menu & Partners"
        ),
        "Restoran menüsü, acenteler, sözleşmeler ve kanal yönetimi" to mapOf(
            AppLanguage.RU to "Меню ресторана, агентства, контракты и управление каналами",
            AppLanguage.TR to "Restoran menüsü, acenteler, sözleşmeler ve kanal yönetimi",
            AppLanguage.EN to "Restaurant menu, agencies, contracts and channel management"
        ),
        "Restoran & Menü Yönetimi" to mapOf(
            AppLanguage.RU to "Управление рестораном и меню",
            AppLanguage.TR to "Restoran & Menü Yönetimi",
            AppLanguage.EN to "Restaurant & Menu Management"
        ),
        "Adisyon menüsü, ürün kategorileri ve oda servisi fiyatları" to mapOf(
            AppLanguage.RU to "Меню счетов, категории товаров и цены на обслуживание в номерах",
            AppLanguage.TR to "Adisyon menüsü, ürün kategorileri ve oda servisi fiyatları",
            AppLanguage.EN to "Check menu, product categories and room service prices"
        ),
        "Acenta Yönetimi" to mapOf(
            AppLanguage.RU to "Управление агентствами",
            AppLanguage.TR to "Acenta Yönetimi",
            AppLanguage.EN to "Agency Management"
        ),
        "Acente kartları, komisyon oranları ve acente kayıtları" to mapOf(
            AppLanguage.RU to "Карточки агентств, ставки комиссий и реестр партнеров",
            AppLanguage.TR to "Acente kartları, komisyon oranları ve acente kayıtları",
            AppLanguage.EN to "Agency cards, commission rates and agency records"
        ),
        "Acente Kontenjan Sözleşmesi" to mapOf(
            AppLanguage.RU to "Контракт квоты агентства",
            AppLanguage.TR to "Acente Kontenjan Sözleşmesi",
            AppLanguage.EN to "Agency Allotment Contract"
        ),
        "Acentelere özel oda blokajları ve dönemsel sözleşmeler" to mapOf(
            AppLanguage.RU to "Бронирование номеров для агентств и сезонные договоры",
            AppLanguage.TR to "Acentelere özel oda blokajları ve dönemsel sözleşmeler",
            AppLanguage.EN to "Room allotments for agencies and seasonal contracts"
        ),
        "Tedarikçi Yönetimi" to mapOf(
            AppLanguage.RU to "Управление поставщиками",
            AppLanguage.TR to "Tedarikçi Yönetimi",
            AppLanguage.EN to "Supplier Management"
        ),
        "Gıda, temizlik ve teknik malzeme tedarikçi kayıtları" to mapOf(
            AppLanguage.RU to "Поставщики продуктов, чистящих средств и техобслуживания",
            AppLanguage.TR to "Gıda, temizlik ve teknik malzeme tedarikçi kayıtları",
            AppLanguage.EN to "Food, cleaning and technical supplies supplier records"
        ),
        "Channel Manager (OTA Senkronizasyonu)" to mapOf(
            AppLanguage.RU to "Channel Manager (Синхронизация с OTA)",
            AppLanguage.TR to "Channel Manager (OTA Senkronizasyonu)",
            AppLanguage.EN to "Channel Manager (OTA Synchronization)"
        ),
        "Booking, Expedia ve Airbnb çift yönlü senkronizasyon" to mapOf(
            AppLanguage.RU to "Двусторонняя синхронизация с Booking, Expedia и Airbnb",
            AppLanguage.TR to "Booking, Expedia ve Airbnb çift yönlü senkronizasyon",
            AppLanguage.EN to "Two-way sync with Booking, Expedia and Airbnb"
        ),
        "OTA Canlı" to mapOf(
            AppLanguage.RU to "OTA Онлайн",
            AppLanguage.TR to "OTA Canlı",
            AppLanguage.EN to "OTA Live"
        ),
        "İK, Güvenlik & Sistem Raporları" to mapOf(
            AppLanguage.RU to "HR, безопасность и системные отчеты",
            AppLanguage.TR to "İK, Güvenlik & Sistem Raporları",
            AppLanguage.EN to "HR, Security & System Reports"
        ),
        "Departmanlar, personel yetkileri, güvenlik ve genel istatistikler" to mapOf(
            AppLanguage.RU to "Отделы, права персонала, безопасность и общая статистика",
            AppLanguage.TR to "Departmanlar, personel yetkileri, güvenlik ve genel istatistikler",
            AppLanguage.EN to "Departments, staff permissions, security and general stats"
        ),
        "Departman Yönetimi" to mapOf(
            AppLanguage.RU to "Управление отделами",
            AppLanguage.TR to "Departman Yönetimi",
            AppLanguage.EN to "Department Management"
        ),
        "Ön büro, kat hizmetleri, muhasebe ve mutfak hiyerarşisi" to mapOf(
            AppLanguage.RU to "Ресепшн, клининг, бухгалтерия и кухня",
            AppLanguage.TR to "Ön büro, kat hizmetleri, muhasebe ve mutfak hiyerarşisi",
            AppLanguage.EN to "Front desk, housekeeping, accounting and kitchen hierarchy"
        ),
        "Personel & Yetki Yönetimi" to mapOf(
            AppLanguage.RU to "Управление персоналом и правами",
            AppLanguage.TR to "Personel & Yetki Yönetimi",
            AppLanguage.EN to "Staff & Permissions Management"
        ),
        "Personel hesapları, sistem rolleri ve erişim izinleri" to mapOf(
            AppLanguage.RU to "Учетные записи, системные роли и права доступа",
            AppLanguage.TR to "Personel hesapları, sistem rolleri ve erişim izinleri",
            AppLanguage.EN to "Staff accounts, system roles and access permissions"
        ),
        "Üye Yönetimi & Kısıtlama" to mapOf(
            AppLanguage.RU to "Управление участниками и ограничения",
            AppLanguage.TR to "Üye Yönetimi & Kısıtlama",
            AppLanguage.EN to "Member Management & Restrictions"
        ),
        "Üye listesi, üyelik kodları ve anlık aktif/pasif kısıtlama" to mapOf(
            AppLanguage.RU to "Список участников, коды и мгновенная блокировка",
            AppLanguage.TR to "Üye listesi, üyelik kodları ve anlık aktif/pasif kısıtlama",
            AppLanguage.EN to "Member list, codes and instant active/passive toggle"
        ),
        "Müşteri Kara Liste Yönetimi" to mapOf(
            AppLanguage.RU to "Управление черным списком",
            AppLanguage.TR to "Müşteri Kara Liste Yönetimi",
            AppLanguage.EN to "Guest Blacklist Management"
        ),
        "Sorunlu misafir kayıtları ve erken rezervasyon engelleme" to mapOf(
            AppLanguage.RU to "Нежелательные гости и блокировка раннего бронирования",
            AppLanguage.TR to "Sorunlu misafir kayıtları ve erken rezervasyon engelleme",
            AppLanguage.EN to "Problematic guest records and early reservation prevention"
        ),
        "Gelişmiş Raporlar & İstatistikler" to mapOf(
            AppLanguage.RU to "Расширенные отчеты и статистика",
            AppLanguage.TR to "Gelişmiş Raporlar & İstatistikler",
            AppLanguage.EN to "Advanced Reports & Statistics"
        ),
        "Doluluk trendleri, departman kârlılığı ve yönetici özetleri" to mapOf(
            AppLanguage.RU to "Тренды загрузки, прибыльность отделов и сводки руководства",
            AppLanguage.TR to "Doluluk trendleri, departman kârlılığı ve yönetici özetleri",
            AppLanguage.EN to "Occupancy trends, department profitability and executive summaries"
        ),
        "Otel Profili & Bilgileri" to mapOf(
            AppLanguage.RU to "Профиль и информация об отеле",
            AppLanguage.TR to "Otel Profili & Bilgileri",
            AppLanguage.EN to "Hotel Profile & Information"
        ),
        "Tesis ve fatura yapılandırma ayarları" to mapOf(
            AppLanguage.RU to "Настройки объекта и реквизитов для счетов",
            AppLanguage.TR to "Tesis ve fatura yapılandırma ayarları",
            AppLanguage.EN to "Facility and invoice configuration settings"
        ),
        "Temel Tesis Bilgileri" to mapOf(
            AppLanguage.RU to "Основная информация об объекте",
            AppLanguage.TR to "Temel Tesis Bilgileri",
            AppLanguage.EN to "Basic Facility Information"
        ),
        "İletişim & Konum Bilgileri" to mapOf(
            AppLanguage.RU to "Контактная информация и адрес",
            AppLanguage.TR to "İletişim & Konum Bilgileri",
            AppLanguage.EN to "Contact & Location Information"
        ),
        "Resmi & Vergi Bilgileri" to mapOf(
            AppLanguage.RU to "Официальные и налоговые данные",
            AppLanguage.TR to "Resmi & Vergi Bilgileri",
            AppLanguage.EN to "Official & Tax Information"
        ),
        "Değişiklikleri Kaydet" to mapOf(
            AppLanguage.RU to "Сохранить изменения",
            AppLanguage.TR to "Değişiklikleri Kaydet",
            AppLanguage.EN to "Save Changes"
        ),
        "Personel" to mapOf(
            AppLanguage.RU to "Сотрудников",
            AppLanguage.TR to "Personel",
            AppLanguage.EN to "Staff"
        ),
        "Personel çalışma saatleri ve nöbet takvimi" to mapOf(
            AppLanguage.RU to "Рабочие часы персонала и график дежурств",
            AppLanguage.TR to "Personel çalışma saatleri ve nöbet takvimi",
            AppLanguage.EN to "Staff working hours and duty schedule"
        ),
        "Vardiya Ekle" to mapOf(
            AppLanguage.RU to "Добавить смену",
            AppLanguage.TR to "Vardiya Ekle",
            AppLanguage.EN to "Add Shift"
        ),
        "Bugünün Vardiyaları" to mapOf(
            AppLanguage.RU to "Смены на сегодня",
            AppLanguage.TR to "Bugünün Vardiyaları",
            AppLanguage.EN to "Today's Shifts"
        ),
        "Planlanan Gün" to mapOf(
            AppLanguage.RU to "Запланированный день",
            AppLanguage.TR to "Planlanan Gün",
            AppLanguage.EN to "Planned Day"
        ),
        "Bu tarihe ait vardiya planı bulunmuyor" to mapOf(
            AppLanguage.RU to "На эту дату нет графика смен",
            AppLanguage.TR to "Bu tarihe ait vardiya planı bulunmuyor",
            AppLanguage.EN to "No shift schedule for this date"
        ),
        "Yeni vardiya eklemek için aşağıdaki butonu kullanabilirsiniz." to mapOf(
            AppLanguage.RU to "Используйте кнопку ниже, чтобы добавить новую смену.",
            AppLanguage.TR to "Yeni vardiya eklemek için aşağıdaki butonu kullanabilirsiniz.",
            AppLanguage.EN to "You can use the button below to add a new shift."
        ),
        "İlk Vardiyayı Ekle" to mapOf(
            AppLanguage.RU to "Добавить первую смену",
            AppLanguage.TR to "İlk Vardiyayı Ekle",
            AppLanguage.EN to "Add First Shift"
        ),
        "Yeni Vardiya Planla" to mapOf(
            AppLanguage.RU to "Запланировать смену",
            AppLanguage.TR to "Yeni Vardiya Planla",
            AppLanguage.EN to "Plan New Shift"
        ),
        "Personel Seçimi" to mapOf(
            AppLanguage.RU to "Выбор сотрудника",
            AppLanguage.TR to "Personel Seçimi",
            AppLanguage.EN to "Staff Selection"
        ),
        "Personel seçiniz..." to mapOf(
            AppLanguage.RU to "Выберите сотрудника...",
            AppLanguage.TR to "Personel seçiniz...",
            AppLanguage.EN to "Select staff..."
        ),
        "Kayıtlı personel bulunamadı" to mapOf(
            AppLanguage.RU to "Зарегистрированные сотрудники не найдены",
            AppLanguage.TR to "Kayıtlı personel bulunamadı",
            AppLanguage.EN to "No registered staff found"
        ),
        "Hızlı Vardiya Şablonu" to mapOf(
            AppLanguage.RU to "Быстрый шаблон смены",
            AppLanguage.TR to "Hızlı Vardiya Şablonu",
            AppLanguage.EN to "Quick Shift Template"
        ),
        "Sabah" to mapOf(
            AppLanguage.RU to "Утро",
            AppLanguage.TR to "Sabah",
            AppLanguage.EN to "Morning"
        ),
        "Akşam" to mapOf(
            AppLanguage.RU to "Вечер",
            AppLanguage.TR to "Akşam",
            AppLanguage.EN to "Evening"
        ),
        "Gece" to mapOf(
            AppLanguage.RU to "Ночь",
            AppLanguage.TR to "Gece",
            AppLanguage.EN to "Night"
        ),
        "Başlangıç (HH:MM)" to mapOf(
            AppLanguage.RU to "Начало (ЧЧ:ММ)",
            AppLanguage.TR to "Başlangıç (HH:MM)",
            AppLanguage.EN to "Start (HH:MM)"
        ),
        "Bitiş (HH:MM)" to mapOf(
            AppLanguage.RU to "Конец (ЧЧ:ММ)",
            AppLanguage.TR to "Bitiş (HH:MM)",
            AppLanguage.EN to "End (HH:MM)"
        ),
        "Görev / Departman" to mapOf(
            AppLanguage.RU to "Должность / Отдел",
            AppLanguage.TR to "Görev / Departman",
            AppLanguage.EN to "Role / Department"
        ),
        "Örn: Resepsiyonist" to mapOf(
            AppLanguage.RU to "Напр: Администратор",
            AppLanguage.TR to "Örn: Resepsiyonist",
            AppLanguage.EN to "Ex: Receptionist"
        ),
        "Ön Büro / Resepsiyon" to mapOf(
            AppLanguage.RU to "Служба приема / Ресепшн",
            AppLanguage.TR to "Ön Büro / Resepsiyon",
            AppLanguage.EN to "Front Desk / Reception"
        ),
        "Restoran & Bar" to mapOf(
            AppLanguage.RU to "Ресторан и бар",
            AppLanguage.TR to "Restoran & Bar",
            AppLanguage.EN to "Restaurant & Bar"
        ),
        "Güvenlik" to mapOf(
            AppLanguage.RU to "Охрана",
            AppLanguage.TR to "Güvenlik",
            AppLanguage.EN to "Security"
        ),
        "Teknik Servis" to mapOf(
            AppLanguage.RU to "Техническая служба",
            AppLanguage.TR to "Teknik Servis",
            AppLanguage.EN to "Technical Service"
        ),
        "Mutfak" to mapOf(
            AppLanguage.RU to "Кухня",
            AppLanguage.TR to "Mutfak",
            AppLanguage.EN to "Kitchen"
        ),
        "Vardiyayı Kaydet" to mapOf(
            AppLanguage.RU to "Сохранить смену",
            AppLanguage.TR to "Vardiyayı Kaydet",
            AppLanguage.EN to "Save Shift"
        ),
        "Aktif / Planlandı" to mapOf(
            AppLanguage.RU to "Активна / Запланирована",
            AppLanguage.TR to "Aktif / Planlandı",
            AppLanguage.EN to "Active / Scheduled"
        ),
        "Beklemede" to mapOf(
            AppLanguage.RU to "В ожидании",
            AppLanguage.TR to "Beklemede",
            AppLanguage.EN to "Pending"
        ),
        "Normal Mesai" to mapOf(
            AppLanguage.RU to "Обычная смена",
            AppLanguage.TR to "Normal Mesai",
            AppLanguage.EN to "Regular Shift"
        ),
        "Vardiyalar" to mapOf(
            AppLanguage.RU to "Смены",
            AppLanguage.TR to "Vardiyalar",
            AppLanguage.EN to "Shifts"
        ),
        "Personel Listesi" to mapOf(
            AppLanguage.RU to "Список сотрудников",
            AppLanguage.TR to "Personel Listesi",
            AppLanguage.EN to "Staff List"
        ),
        "Resepsiyon" to mapOf(
            AppLanguage.RU to "Ресепшн",
            AppLanguage.TR to "Resepsiyon",
            AppLanguage.EN to "Reception"
        ),
        "Otel çalışanları ve departman dağılımı" to mapOf(
            AppLanguage.RU to "Распределение сотрудников и отделов отеля",
            AppLanguage.TR to "Otel çalışanları ve departman dağılımı",
            AppLanguage.EN to "Hotel staff and department distribution"
        ),
        "Personel Ekle" to mapOf(
            AppLanguage.RU to "Добавить сотрудника",
            AppLanguage.TR to "Personel Ekle",
            AppLanguage.EN to "Add Staff"
        ),
        "Personel adı veya departman ara..." to mapOf(
            AppLanguage.RU to "Поиск по имени или отделу...",
            AppLanguage.TR to "Personel adı veya departman ara...",
            AppLanguage.EN to "Search staff name or department..."
        ),
        "Arama kriterlerine uygun çalışan bulunamadı." to mapOf(
            AppLanguage.RU to "Сотрудники по критериям поиска не найдены.",
            AppLanguage.TR to "Arama kriterlerine uygun çalışan bulunamadı.",
            AppLanguage.EN to "No staff found matching search criteria."
        ),
        "Yeni personel eklemek için aşağıdaki butonu kullanabilirsiniz." to mapOf(
            AppLanguage.RU to "Используйте кнопку ниже, чтобы добавить нового сотрудника.",
            AppLanguage.TR to "Yeni personel eklemek için aşağıdaki butonu kullanabilirsiniz.",
            AppLanguage.EN to "You can use the button below to add a new staff member."
        ),
        "Personeli Kaydet" to mapOf(
            AppLanguage.RU to "Сохранить сотрудника",
            AppLanguage.TR to "Personeli Kaydet",
            AppLanguage.EN to "Save Staff"
        ),
        "Genel Kadro" to mapOf(
            AppLanguage.RU to "Основной штат",
            AppLanguage.TR to "Genel Kadro",
            AppLanguage.EN to "General Staff"
        ),
        "Aktif Personel" to mapOf(
            AppLanguage.RU to "Активный сотрудник",
            AppLanguage.TR to "Aktif Personel",
            AppLanguage.EN to "Active Staff"
        ),
        "Vardiyaya Uygun" to mapOf(
            AppLanguage.RU to "Доступен для смены",
            AppLanguage.TR to "Vardiyaya Uygun",
            AppLanguage.EN to "Available for Shift"
        ),
        "Envanter & Stok Yönetimi" to mapOf(
            AppLanguage.RU to "Управление складом и инвентарем",
            AppLanguage.TR to "Envanter & Stok Yönetimi",
            AppLanguage.EN to "Inventory & Stock Management"
        ),
        "Otel Depo ve Satın Alma Takibi" to mapOf(
            AppLanguage.RU to "Учет склада отеля и закупок",
            AppLanguage.TR to "Otel Depo ve Satın Alma Takibi",
            AppLanguage.EN to "Hotel Warehouse & Purchasing Tracking"
        ),
        "Stok Listesi" to mapOf(
            AppLanguage.RU to "Список товаров",
            AppLanguage.TR to "Stok Listesi",
            AppLanguage.EN to "Stock List"
        ),
        "Satın Alma" to mapOf(
            AppLanguage.RU to "Закупки",
            AppLanguage.TR to "Satın Alma",
            AppLanguage.EN to "Purchasing"
        ),
        "Yeni Ürün Ekle" to mapOf(
            AppLanguage.RU to "Добавить товар",
            AppLanguage.TR to "Yeni Ürün Ekle",
            AppLanguage.EN to "Add New Product"
        ),
        "Toplam Ürün Çeşidi" to mapOf(
            AppLanguage.RU to "Всего наименований",
            AppLanguage.TR to "Toplam Ürün Çeşidi",
            AppLanguage.EN to "Total Product Types"
        ),
        "Kritik Stok Uyarısı" to mapOf(
            AppLanguage.RU to "Критический остаток",
            AppLanguage.TR to "Kritik Stok Uyarısı",
            AppLanguage.EN to "Critical Stock Alert"
        ),
        "Tükenen Ürünler" to mapOf(
            AppLanguage.RU to "Закончившиеся товары",
            AppLanguage.TR to "Tükenen Ürünler",
            AppLanguage.EN to "Out of Stock Products"
        ),
        "Ürün adı veya kategori ara..." to mapOf(
            AppLanguage.RU to "Поиск товара или категории...",
            AppLanguage.TR to "Ürün adı veya kategori ara...",
            AppLanguage.EN to "Search product name or category..."
        ),
        "Kritik" to mapOf(
            AppLanguage.RU to "Критический",
            AppLanguage.TR to "Kritik",
            AppLanguage.EN to "Critical"
        ),
        "Temizlik" to mapOf(
            AppLanguage.RU to "Уборка",
            AppLanguage.TR to "Temizlik",
            AppLanguage.EN to "Cleaning"
        ),
        "Minibar" to mapOf(
            AppLanguage.RU to "Минибар",
            AppLanguage.TR to "Minibar",
            AppLanguage.EN to "Minibar"
        ),
        "Kayıtlı Stok Ürünü Bulunmuyor" to mapOf(
            AppLanguage.RU to "Нет зарегистрированных товаров",
            AppLanguage.TR to "Kayıtlı Stok Ürünü Bulunmuyor",
            AppLanguage.EN to "No Registered Stock Items"
        ),
        "Temizlik malzemeleri, minibar ve buklet ürünlerini takip etmek için ilk ürünü ekleyin." to mapOf(
            AppLanguage.RU to "Добавьте первый товар для учета чистящих средств, мини-бара и косметических принадлежностей.",
            AppLanguage.TR to "Temizlik malzemeleri, minibar ve buklet ürünlerini takip etmek için ilk ürünü ekleyin.",
            AppLanguage.EN to "Add the first product to track cleaning supplies, minibar and booklet items."
        ),
        "İlk Ürünü Ekle" to mapOf(
            AppLanguage.RU to "Добавить первый товар",
            AppLanguage.TR to "İlk Ürünü Ekle",
            AppLanguage.EN to "Add First Product"
        ),
        "Yeni Envanter Ürünü Ekle" to mapOf(
            AppLanguage.RU to "Добавить товар на склад",
            AppLanguage.TR to "Yeni Envanter Ürünü Ekle",
            AppLanguage.EN to "Add New Inventory Item"
        ),
        "Ürün Adı" to mapOf(
            AppLanguage.RU to "Наименование товара",
            AppLanguage.TR to "Ürün Adı",
            AppLanguage.EN to "Product Name"
        ),
        "Örn: Banyo Havlusu veya Çamaşır Deterjanı" to mapOf(
            AppLanguage.RU to "Напр.: Банное полотенце или Стиральный порошок",
            AppLanguage.TR to "Örn: Banyo Havlusu veya Çamaşır Deterjanı",
            AppLanguage.EN to "Ex: Bath Towel or Laundry Detergent"
        ),
        "Temizlik Malzemesi" to mapOf(
            AppLanguage.RU to "Чистящие средства",
            AppLanguage.TR to "Temizlik Malzemesi",
            AppLanguage.EN to "Cleaning Supplies"
        ),
        "Buklet / Tekstil" to mapOf(
            AppLanguage.RU to "Косметика и текстиль",
            AppLanguage.TR to "Buklet / Tekstil",
            AppLanguage.EN to "Amenities & Textile"
        ),
        "Minibar & İçecek" to mapOf(
            AppLanguage.RU to "Мини-бар и напитки",
            AppLanguage.TR to "Minibar & İçecek",
            AppLanguage.EN to "Minibar & Beverage"
        ),
        "Mutfak & Gıda" to mapOf(
            AppLanguage.RU to "Кухня и продукты",
            AppLanguage.TR to "Mutfak & Gıda",
            AppLanguage.EN to "Kitchen & Food"
        ),
        "Bakım & Teknik Parça" to mapOf(
            AppLanguage.RU to "Техобслуживание и запчасти",
            AppLanguage.TR to "Bakım & Teknik Parça",
            AppLanguage.EN to "Maintenance & Technical Parts"
        ),
        "Genel Sarf Malzeme" to mapOf(
            AppLanguage.RU to "Расходные материалы",
            AppLanguage.TR to "Genel Sarf Malzeme",
            AppLanguage.EN to "General Consumables"
        ),
        "Mevcut Miktar" to mapOf(
            AppLanguage.RU to "Текущее количество",
            AppLanguage.TR to "Mevcut Miktar",
            AppLanguage.EN to "Current Quantity"
        ),
        "Örn: 50" to mapOf(
            AppLanguage.RU to "Напр: 50",
            AppLanguage.TR to "Örn: 50",
            AppLanguage.EN to "Ex: 50"
        ),
        "Birim" to mapOf(
            AppLanguage.RU to "Ед. изм.",
            AppLanguage.TR to "Birim",
            AppLanguage.EN to "Unit"
        ),
        "Adet" to mapOf(
            AppLanguage.RU to "Шт.",
            AppLanguage.TR to "Adet",
            AppLanguage.EN to "Pcs"
        ),
        "Koli" to mapOf(
            AppLanguage.RU to "Кор.",
            AppLanguage.TR to "Koli",
            AppLanguage.EN to "Box"
        ),
        "Litre" to mapOf(
            AppLanguage.RU to "Литр",
            AppLanguage.TR to "Litre",
            AppLanguage.EN to "Liter"
        ),
        "Kg" to mapOf(
            AppLanguage.RU to "Кг",
            AppLanguage.TR to "Kg",
            AppLanguage.EN to "Kg"
        ),
        "Paket" to mapOf(
            AppLanguage.RU to "Уп.",
            AppLanguage.TR to "Paket",
            AppLanguage.EN to "Pack"
        ),
        "Şişe" to mapOf(
            AppLanguage.RU to "Бут.",
            AppLanguage.TR to "Şişe",
            AppLanguage.EN to "Bottle"
        ),
        "Kritik Stok Uyarısı Eşiği" to mapOf(
            AppLanguage.RU to "Порог критического остатка",
            AppLanguage.TR to "Kritik Stok Uyarısı Eşiği",
            AppLanguage.EN to "Critical Stock Alert Threshold"
        ),
        "Örn: 10" to mapOf(
            AppLanguage.RU to "Напр: 10",
            AppLanguage.TR to "Örn: 10",
            AppLanguage.EN to "Ex: 10"
        ),
        "Lütfen ürün adını giriniz." to mapOf(
            AppLanguage.RU to "Пожалуйста, введите название товара.",
            AppLanguage.TR to "Lütfen ürün adını giriniz.",
            AppLanguage.EN to "Please enter product name."
        ),
        "Ürünü Ekle" to mapOf(
            AppLanguage.RU to "Добавить товар",
            AppLanguage.TR to "Ürünü Ekle",
            AppLanguage.EN to "Add Product"
        ),
        "Stokta" to mapOf(
            AppLanguage.RU to "В наличии",
            AppLanguage.TR to "Stokta",
            AppLanguage.EN to "In Stock"
        ),
        "Kritik Stok" to mapOf(
            AppLanguage.RU to "Критический остаток",
            AppLanguage.TR to "Kritik Stok",
            AppLanguage.EN to "Low Stock"
        ),
        "Tükendi" to mapOf(
            AppLanguage.RU to "Закончился",
            AppLanguage.TR to "Tükendi",
            AppLanguage.EN to "Out of Stock"
        ),
        "Kritik Eşik" to mapOf(
            AppLanguage.RU to "Критический порог",
            AppLanguage.TR to "Kritik Eşik",
            AppLanguage.EN to "Critical Threshold"
        ),
        "Stok Girişi" to mapOf(
            AppLanguage.RU to "Приход на склад",
            AppLanguage.TR to "Stok Girişi",
            AppLanguage.EN to "Stock Entry"
        ),
        "Stok Çıkışı" to mapOf(
            AppLanguage.RU to "Расход со склада",
            AppLanguage.TR to "Stok Çıkışı",
            AppLanguage.EN to "Stock Outflow"
        ),
        "Depoya eklenecek miktarı giriniz:" to mapOf(
            AppLanguage.RU to "Введите количество для добавления на склад:",
            AppLanguage.TR to "Depoya eklenecek miktarı giriniz:",
            AppLanguage.EN to "Enter quantity to add to stock:"
        ),
        "Depodan düşülecek miktarı giriniz:" to mapOf(
            AppLanguage.RU to "Введите количество для списания со склада:",
            AppLanguage.TR to "Depodan düşülecek miktarı giriniz:",
            AppLanguage.EN to "Enter quantity to reduce from stock:"
        ),
        "Stoğa Ekle" to mapOf(
            AppLanguage.RU to "Добавить на склад",
            AppLanguage.TR to "Stoğa Ekle",
            AppLanguage.EN to "Add to Stock"
        ),
        "Stoktan Düş" to mapOf(
            AppLanguage.RU to "Списать со склада",
            AppLanguage.TR to "Stoktan Düş",
            AppLanguage.EN to "Deduct from Stock"
        ),
        "Envanter & Stok" to mapOf(
            AppLanguage.RU to "Склад и инвентарь",
            AppLanguage.TR to "Envanter & Stok",
            AppLanguage.EN to "Inventory & Stock"
        ),
        "Bakım & Onarım" to mapOf(
            AppLanguage.RU to "Техобслуживание",
            AppLanguage.TR to "Bakım & Onarım",
            AppLanguage.EN to "Maintenance"
        ),
        "Vardiya Planı" to mapOf(
            AppLanguage.RU to "График смен",
            AppLanguage.TR to "Vardiya Planı",
            AppLanguage.EN to "Shift Schedule"
        ),
        "New Order" to mapOf(
            AppLanguage.RU to "Новый заказ",
            AppLanguage.TR to "Yeni Sipariş",
            AppLanguage.EN to "New Order"
        ),
        "Create New Order" to mapOf(
            AppLanguage.RU to "Создать новый заказ",
            AppLanguage.TR to "Yeni Sipariş Oluştur",
            AppLanguage.EN to "Create New Order"
        ),
        "Select Supplier" to mapOf(
            AppLanguage.RU to "Выберите поставщика",
            AppLanguage.TR to "Tedarikçi Seçin",
            AppLanguage.EN to "Select Supplier"
        ),
        "Items" to mapOf(
            AppLanguage.RU to "Позиции",
            AppLanguage.TR to "Ürünler",
            AppLanguage.EN to "Items"
        ),
        "Item" to mapOf(
            AppLanguage.RU to "Товар",
            AppLanguage.TR to "Ürün",
            AppLanguage.EN to "Item"
        ),
        "Quantity" to mapOf(
            AppLanguage.RU to "Количество",
            AppLanguage.TR to "Miktar",
            AppLanguage.EN to "Quantity"
        ),
        "+ Add to List" to mapOf(
            AppLanguage.RU to "+ Добавить в список",
            AppLanguage.TR to "+ Listeye Ekle",
            AppLanguage.EN to "+ Add to List"
        ),
        "Create" to mapOf(
            AppLanguage.RU to "Создать",
            AppLanguage.TR to "Oluştur",
            AppLanguage.EN to "Create"
        ),
        "Delivered" to mapOf(
            AppLanguage.RU to "Доставлено",
            AppLanguage.TR to "Teslim Edildi",
            AppLanguage.EN to "Delivered"
        ),
        "Mark as Delivered" to mapOf(
            AppLanguage.RU to "Отметить как доставлено",
            AppLanguage.TR to "Teslim Edildi Olarak İşaretle",
            AppLanguage.EN to "Mark as Delivered"
        ),
        "Haftalık Doluluk Oranı" to mapOf(
            AppLanguage.RU to "Еженедельная заполняемость",
            AppLanguage.TR to "Haftalık Doluluk Oranı",
            AppLanguage.EN to "Weekly Occupancy Rate"
        ),
        "Aylık Doluluk Oranı" to mapOf(
            AppLanguage.RU to "Ежемесячная заполняемость",
            AppLanguage.TR to "Aylık Doluluk Oranı",
            AppLanguage.EN to "Monthly Occupancy Rate"
        ),
        "Yıllık Doluluk Oranı" to mapOf(
            AppLanguage.RU to "Годовая заполняемость",
            AppLanguage.TR to "Yıllık Doluluk Oranı",
            AppLanguage.EN to "Yearly Occupancy Rate"
        ),
        "Weekly Occupancy Rate" to mapOf(
            AppLanguage.RU to "Еженедельная заполняемость",
            AppLanguage.TR to "Haftalık Doluluk Oranı",
            AppLanguage.EN to "Weekly Occupancy Rate"
        ),
        "Monthly Occupancy Rate" to mapOf(
            AppLanguage.RU to "Ежемесячная заполняемость",
            AppLanguage.TR to "Aylık Doluluk Oranı",
            AppLanguage.EN to "Monthly Occupancy Rate"
        ),
        "Yearly Occupancy Rate" to mapOf(
            AppLanguage.RU to "Годовая заполняемость",
            AppLanguage.TR to "Yıllık Doluluk Oranı",
            AppLanguage.EN to "Yearly Occupancy Rate"
        ),
        "Gösterge Paneli" to mapOf(
            AppLanguage.RU to "Панель управления",
            AppLanguage.TR to "Gösterge Paneli",
            AppLanguage.EN to "Dashboard"
        ),
        "Ön Büro (Rezervasyon)" to mapOf(
            AppLanguage.RU to "Служба приема и размещения (Бронирование)",
            AppLanguage.TR to "Ön Büro (Rezervasyon)",
            AppLanguage.EN to "Front Office (Reservation)"
        ),
        "Ön Büro" to mapOf(
            AppLanguage.RU to "Стойка регистрации",
            AppLanguage.TR to "Ön Büro",
            AppLanguage.EN to "Front Desk"
        ),
        "Oda Durumları" to mapOf(
            AppLanguage.RU to "Статус номеров",
            AppLanguage.TR to "Oda Durumları",
            AppLanguage.EN to "Room Status"
        ),
        "Kat Hizmetleri" to mapOf(
            AppLanguage.RU to "Уборка и обслуживание",
            AppLanguage.TR to "Kat Hizmetleri",
            AppLanguage.EN to "Housekeeping"
        ),
        "Kasa & Finans" to mapOf(
            AppLanguage.RU to "Финансы и касса",
            AppLanguage.TR to "Kasa & Finans",
            AppLanguage.EN to "Finance & Cash"
        ),
        "Kasa İşlemleri" to mapOf(
            AppLanguage.RU to "Кассовые операции",
            AppLanguage.TR to "Kasa İşlemleri",
            AppLanguage.EN to "Cash Operations"
        ),
        "Envanter" to mapOf(
            AppLanguage.RU to "Инвентарь",
            AppLanguage.TR to "Envanter",
            AppLanguage.EN to "Inventory"
        ),
        "Envanter Yönetimi" to mapOf(
            AppLanguage.RU to "Управление инвентарем",
            AppLanguage.TR to "Envanter Yönetimi",
            AppLanguage.EN to "Inventory Management"
        ),
        "Yardım & Destek" to mapOf(
            AppLanguage.RU to "Помощь и поддержка",
            AppLanguage.TR to "Yardım & Destek",
            AppLanguage.EN to "Help & Support"
        ),
        "Oturumu Kapat" to mapOf(
            AppLanguage.RU to "Выйти из системы",
            AppLanguage.TR to "Oturumu Kapat",
            AppLanguage.EN to "Logout"
        ),
        "Dolu Odalar" to mapOf(
            AppLanguage.RU to "Занятые номера",
            AppLanguage.TR to "Dolu Odalar",
            AppLanguage.EN to "Occupied Rooms"
        ),
        "Oda Özet" to mapOf(
            AppLanguage.RU to "Сводка по номерам",
            AppLanguage.TR to "Oda Özet",
            AppLanguage.EN to "Room Summary"
        ),
        "Kâr / Zarar Raporu" to mapOf(
            AppLanguage.RU to "Отчет о прибылях и убытках",
            AppLanguage.TR to "Kâr / Zarar Raporu",
            AppLanguage.EN to "Profit & Loss Report"
        ),
        "Cari Hesaplar" to mapOf(
            AppLanguage.RU to "Текущие счета",
            AppLanguage.TR to "Cari Hesaplar",
            AppLanguage.EN to "Current Accounts"
        ),
        "Acenta Sözleşmeleri" to mapOf(
            AppLanguage.RU to "Договоры с агентствами",
            AppLanguage.TR to "Acenta Sözleşmeleri",
            AppLanguage.EN to "Agency Contracts"
        ),
        "Kara Liste" to mapOf(
            AppLanguage.RU to "Черный список",
            AppLanguage.TR to "Kara Liste",
            AppLanguage.EN to "Blacklist"
        ),
        "Rezervasyonlar" to mapOf(
            AppLanguage.RU to "Бронирования",
            AppLanguage.TR to "Rezervasyonlar",
            AppLanguage.EN to "Reservations"
        ),
        "Çıkış Yapacaklar (Check-Out)" to mapOf(
            AppLanguage.RU to "Ожидаемый выезд (Check-Out)",
            AppLanguage.TR to "Çıkış Yapacaklar (Check-Out)",
            AppLanguage.EN to "Check-Out List"
        ),
        "Giriş Yapacaklar (Check-In)" to mapOf(
            AppLanguage.RU to "Ожидаемый заезд (Check-In)",
            AppLanguage.TR to "Giriş Yapacaklar (Check-In)",
            AppLanguage.EN to "Check-In List"
        ),
        "Takvim" to mapOf(
            AppLanguage.RU to "Календарь",
            AppLanguage.TR to "Takvim",
            AppLanguage.EN to "Calendar"
        ),
        "Dil / Language" to mapOf(
            AppLanguage.RU to "Язык",
            AppLanguage.TR to "Dil",
            AppLanguage.EN to "Language"
        ),
        "Açık Masalar" to mapOf(
            AppLanguage.RU to "Открытые Столы",
            AppLanguage.TR to "Açık Masalar",
            AppLanguage.EN to "Open Tables"
        ),
        "Masa Aktif" to mapOf(
            AppLanguage.RU to "Активные Столы",
            AppLanguage.TR to "Masa Aktif",
            AppLanguage.EN to "Active Tables"
        ),
        "Yeni Masa Aç" to mapOf(
            AppLanguage.RU to "Открыть Новый Стол",
            AppLanguage.TR to "Yeni Masa Aç",
            AppLanguage.EN to "Open New Table"
        ),
        "Restoran POS" to mapOf(
            AppLanguage.RU to "POS Ресторан",
            AppLanguage.TR to "Restoran POS",
            AppLanguage.EN to "Restaurant POS"
        ),
        "Sipariş Adisyonu" to mapOf(
            AppLanguage.RU to "Счет Заказа",
            AppLanguage.TR to "Sipariş Adisyonu",
            AppLanguage.EN to "Order Bill / Check"
        ),
        "Manuel Ürün / Hizmet Ekle" to mapOf(
            AppLanguage.RU to "Добавить Твар Вручную",
            AppLanguage.TR to "Manuel Ürün / Hizmet Ekle",
            AppLanguage.EN to "Add Manual Item / Service"
        ),
        "Beklet" to mapOf(
            AppLanguage.RU to "Отложить",
            AppLanguage.TR to "Beklet",
            AppLanguage.EN to "Hold Order"
        ),
        "Ödeme Al" to mapOf(
            AppLanguage.RU to "Принять Оплату",
            AppLanguage.TR to "Ödeme Al",
            AppLanguage.EN to "Take Payment"
        ),
        "Ödeme Yöntemi" to mapOf(
            AppLanguage.RU to "Способ Оплаты",
            AppLanguage.TR to "Ödeme Yöntemi",
            AppLanguage.EN to "Payment Method"
        ),
        "Nakit / Kredi Kartı" to mapOf(
            AppLanguage.RU to "Наличные / Карта",
            AppLanguage.TR to "Nakit / Kredi Kartı",
            AppLanguage.EN to "Cash / Credit Card"
        ),
        "Odaya Yaz" to mapOf(
            AppLanguage.RU to "Записать на Номер",
            AppLanguage.TR to "Odaya Yaz",
            AppLanguage.EN to "Charge to Room"
        ),
        "Oda Seçimi" to mapOf(
            AppLanguage.RU to "Выбор Номера",
            AppLanguage.TR to "Oda Seçimi",
            AppLanguage.EN to "Room Selection"
        ),
        "Ödemeyi Tamamla" to mapOf(
            AppLanguage.RU to "Завершить Оплату",
            AppLanguage.TR to "Ödemeyi Tamamla",
            AppLanguage.EN to "Complete Payment"
        ),
        "Tümü" to mapOf(
            AppLanguage.RU to "Все",
            AppLanguage.TR to "Tümü",
            AppLanguage.EN to "All"
        ),
        "Yiyecek" to mapOf(
            AppLanguage.RU to "Еда",
            AppLanguage.TR to "Yiyecek",
            AppLanguage.EN to "Food"
        ),
        "İçecek" to mapOf(
            AppLanguage.RU to "Напитки",
            AppLanguage.TR to "İçecek",
            AppLanguage.EN to "Beverages"
        ),
        "Tatlı" to mapOf(
            AppLanguage.RU to "Десерты",
            AppLanguage.TR to "Tatlı",
            AppLanguage.EN to "Desserts"
        ),
        "Alkol" to mapOf(
            AppLanguage.RU to "Алкоголь",
            AppLanguage.TR to "Alkol",
            AppLanguage.EN to "Alcohol"
        ),
        "Diğer" to mapOf(
            AppLanguage.RU to "Другое",
            AppLanguage.TR to "Diğer",
            AppLanguage.EN to "Other"
        ),
        "Ara Toplam" to mapOf(
            AppLanguage.RU to "Промежуточный Итог",
            AppLanguage.TR to "Ara Toplam",
            AppLanguage.EN to "Subtotal"
        ),
        "Toplam" to mapOf(
            AppLanguage.RU to "Итого",
            AppLanguage.TR to "Toplam",
            AppLanguage.EN to "Total"
        ),
        "AÇIK" to mapOf(
            AppLanguage.RU to "ОТКРЫТ",
            AppLanguage.TR to "AÇIK",
            AppLanguage.EN to "OPEN"
        ),
        "Geri" to mapOf(
            AppLanguage.RU to "Назад",
            AppLanguage.TR to "Geri",
            AppLanguage.EN to "Back"
        ),
        "Kapat" to mapOf(
            AppLanguage.RU to "Закрыть",
            AppLanguage.TR to "Kapat",
            AppLanguage.EN to "Close"
        ),
        "Kaydet" to mapOf(
            AppLanguage.RU to "Сохранить",
            AppLanguage.TR to "Kaydet",
            AppLanguage.EN to "Save"
        ),
        "İptal" to mapOf(
            AppLanguage.RU to "Отмена",
            AppLanguage.TR to "İptal",
            AppLanguage.EN to "Cancel"
        ),
        "Sil" to mapOf(
            AppLanguage.RU to "Удалить",
            AppLanguage.TR to "Sil",
            AppLanguage.EN to "Delete"
        ),
        "Restoran & Oda Servisi Menü Yönetimi" to mapOf(
            AppLanguage.RU to "Управление Меню Ресторана и Обслуживания Номеров",
            AppLanguage.TR to "Restoran & Oda Servisi Menü Yönetimi",
            AppLanguage.EN to "Restaurant & Room Service Menu Management"
        ),
        "Fiyat Periyotları" to mapOf(
            AppLanguage.RU to "Периоды Цен",
            AppLanguage.TR to "Fiyat Periyotları",
            AppLanguage.EN to "Pricing Periods"
        ),
        "KBS Kimlik Bildirim Formu" to mapOf(
            AppLanguage.RU to "Форма Уведомления МВД / Регистрация Гостей",
            AppLanguage.TR to "KBS Kimlik Bildirim Formu",
            AppLanguage.EN to "Guest Identity & Police Notification Form"
        ),
        "Emniyet & Jandarma Otomatik Bildirimi" to mapOf(
            AppLanguage.RU to "Автоматическая регистрация в УВМ МВД РФ",
            AppLanguage.TR to "Emniyet & Jandarma Otomatik Bildirimi",
            AppLanguage.EN to "Automatic Police & MVD Notification"
        ),
        "TC Vatandaşı" to mapOf(
            AppLanguage.RU to "Гражданин РФ",
            AppLanguage.TR to "TC Vatandaşı",
            AppLanguage.EN to "Citizen"
        ),
        "Yabancı" to mapOf(
            AppLanguage.RU to "Иностранный гражданин",
            AppLanguage.TR to "Yabancı",
            AppLanguage.EN to "Foreigner"
        ),
        "Yabancı (Pasaport)" to mapOf(
            AppLanguage.RU to "Иностранный гражданин (Паспорт)",
            AppLanguage.TR to "Yabancı (Pasaport)",
            AppLanguage.EN to "Foreigner (Passport)"
        ),
        "TC Kimlik No" to mapOf(
            AppLanguage.RU to "Идентификационный номер (СНИЛС / ИНН / Паспорт)",
            AppLanguage.TR to "TC Kimlik No",
            AppLanguage.EN to "National ID / Passport No"
        ),
        "Pasaport No" to mapOf(
            AppLanguage.RU to "Заграничный Паспорт / Миграционная Карта",
            AppLanguage.TR to "Pasaport No",
            AppLanguage.EN to "Passport / Migration Card"
        ),
        "KBS Bildirimi Başarıyla Yapıldı" to mapOf(
            AppLanguage.RU to "Регистрация в МВД РФ успешно отправлена",
            AppLanguage.TR to "KBS Bildirimi Başarıyla Yapıldı",
            AppLanguage.EN to "Police Notification Sent Successfully"
        ),
        "KBS Formu Doldurulmayı Bekliyor" to mapOf(
            AppLanguage.RU to "Ожидает отправки в МВД РФ",
            AppLanguage.TR to "KBS Formu Doldurulmayı Bekliyor",
            AppLanguage.EN to "Awaiting Submission"
        ),
        "Hoş Geldiniz" to mapOf(
            AppLanguage.RU to "Добро пожаловать",
            AppLanguage.TR to "Hoş Geldiniz",
            AppLanguage.EN to "Welcome"
        ),
        "Otel Yönetim Sistemine Giriş Yapın" to mapOf(
            AppLanguage.RU to "Вход в систему управления отелем",
            AppLanguage.TR to "Otel Yönetim Sistemine Giriş Yapın",
            AppLanguage.EN to "Sign in to Hotel Management System"
        ),
        "E-posta" to mapOf(
            AppLanguage.RU to "Эл. почта / Логин",
            AppLanguage.TR to "E-posta",
            AppLanguage.EN to "Email"
        ),
        "Şifre" to mapOf(
            AppLanguage.RU to "Пароль",
            AppLanguage.TR to "Şifre",
            AppLanguage.EN to "Password"
        ),
        "Giriş Yap" to mapOf(
            AppLanguage.RU to "Войти",
            AppLanguage.TR to "Giriş Yap",
            AppLanguage.EN to "Sign In"
        ),
        "Yeni Kayıt" to mapOf(
            AppLanguage.RU to "Регистрация",
            AppLanguage.TR to "Yeni Kayıt",
            AppLanguage.EN to "Register"
        ),
        "Şifremi Unuttum" to mapOf(
            AppLanguage.RU to "Забыли пароль?",
            AppLanguage.TR to "Şifremi Unuttum",
            AppLanguage.EN to "Forgot Password?"
        ),
        "Kendi otel sisteminizi oluşturun" to mapOf(
            AppLanguage.RU to "Создайте свою систему отеля",
            AppLanguage.TR to "Kendi otel sisteminizi oluşturun",
            AppLanguage.EN to "Create your hotel system"
        ),
        "Otel Adı" to mapOf(
            AppLanguage.RU to "Название отеля",
            AppLanguage.TR to "Otel Adı",
            AppLanguage.EN to "Hotel Name"
        ),
        "Kayıt Ol" to mapOf(
            AppLanguage.RU to "Зарегистрироваться",
            AppLanguage.TR to "Kayıt Ol",
            AppLanguage.EN to "Sign Up"
        ),
        "Zaten hesabınız var mı? Giriş Yap" to mapOf(
            AppLanguage.RU to "Уже есть аккаунт? Войти",
            AppLanguage.TR to "Zaten hesabınız var mı? Giriş Yap",
            AppLanguage.EN to "Already have an account? Sign In"
        ),
        "Şifre Sıfırlama" to mapOf(
            AppLanguage.RU to "Сброс пароля",
            AppLanguage.TR to "Şifre Sıfırlama",
            AppLanguage.EN to "Reset Password"
        ),
        "Sıfırlama Bağlantısı Gönder" to mapOf(
            AppLanguage.RU to "Отправить ссылку для сброса",
            AppLanguage.TR to "Sıfırlama Bağlantısı Gönder",
            AppLanguage.EN to "Send Reset Link"
        ),
        "Giriş Ekranına Dön" to mapOf(
            AppLanguage.RU to "Вернуться к входу",
            AppLanguage.TR to "Giriş Ekranına Dön",
            AppLanguage.EN to "Back to Login"
        ),
        "HotelOS Yönetim Paneli" to mapOf(
            AppLanguage.RU to "Панель управления HotelOS",
            AppLanguage.TR to "HotelOS Yönetim Paneli",
            AppLanguage.EN to "HotelOS Management Panel"
        ),
        "Otel operasyonları ve anlık doluluk durumu" to mapOf(
            AppLanguage.RU to "Операции отеля и текущая загрузка",
            AppLanguage.TR to "Otel operasyonları ve anlık doluluk durumu",
            AppLanguage.EN to "Hotel operations and live occupancy status"
        ),
        "Üye Yönetimi" to mapOf(
            AppLanguage.RU to "Управление участниками",
            AppLanguage.TR to "Üye Yönetimi",
            AppLanguage.EN to "Member Management"
        ),
        "Rezervasyon Oluştur" to mapOf(
            AppLanguage.RU to "Создать бронирование",
            AppLanguage.TR to "Rezervasyon Oluştur",
            AppLanguage.EN to "Create Reservation"
        ),
        "Hızlı Giriş" to mapOf(
            AppLanguage.RU to "Быстрый заезд",
            AppLanguage.TR to "Hızlı Giriş",
            AppLanguage.EN to "Quick Check-in"
        ),
        "Giriş İşlemleri" to mapOf(
            AppLanguage.RU to "Процесс заезда",
            AppLanguage.TR to "Giriş İşlemleri",
            AppLanguage.EN to "Check-in Operations"
        ),
        "Hızlı Çıkış" to mapOf(
            AppLanguage.RU to "Быстрый выезд",
            AppLanguage.TR to "Hızlı Çıkış",
            AppLanguage.EN to "Quick Check-out"
        ),
        "Çıkış & Folio" to mapOf(
            AppLanguage.RU to "Выезд и фолио",
            AppLanguage.TR to "Çıkış & Folio",
            AppLanguage.EN to "Check-out & Folio"
        ),
        "Canlı Oda Rack" to mapOf(
            AppLanguage.RU to "Стойка номеров",
            AppLanguage.TR to "Canlı Oda Rack",
            AppLanguage.EN to "Live Room Rack"
        ),
        "Giriş Beklenenler" to mapOf(
            AppLanguage.RU to "Ожидаемый заезд",
            AppLanguage.TR to "Giriş Beklenenler",
            AppLanguage.EN to "Expected Check-ins"
        ),
        "Bugün Beklenen Giriş" to mapOf(
            AppLanguage.RU to "Ожидается заезд сегодня",
            AppLanguage.TR to "Bugün Beklenen Giriş",
            AppLanguage.EN to "Expected Today"
        ),
        "Çıkış Beklenenler" to mapOf(
            AppLanguage.RU to "Ожидаемый выезд",
            AppLanguage.TR to "Çıkış Beklenenler",
            AppLanguage.EN to "Expected Check-outs"
        ),
        "Bugün Beklenen Çıkış" to mapOf(
            AppLanguage.RU to "Ожидается выезд сегодня",
            AppLanguage.TR to "Bugün Beklenen Çıkış",
            AppLanguage.EN to "Expected Today"
        ),
        "Müsait Odalar" to mapOf(
            AppLanguage.RU to "Свободные номера",
            AppLanguage.TR to "Müsait Odalar",
            AppLanguage.EN to "Available Rooms"
        ),
        "Satışa Hazır / Müsait" to mapOf(
            AppLanguage.RU to "Готовы к продаже / Свободны",
            AppLanguage.TR to "Satışa Hazır / Müsait",
            AppLanguage.EN to "Ready for Sale / Vacant"
        ),
        "Temizlik / Bakım" to mapOf(
            AppLanguage.RU to "Уборка и обслуживание",
            AppLanguage.TR to "Temizlik / Bakım",
            AppLanguage.EN to "Cleaning / Maintenance"
        ),
        "Kirli / Bakımda Olanlar" to mapOf(
            AppLanguage.RU to "Грязные / На обслуживании",
            AppLanguage.TR to "Kirli / Bakımda Olanlar",
            AppLanguage.EN to "Dirty / Under Maintenance"
        ),
        "Şu An Dolu / Konaklamada" to mapOf(
            AppLanguage.RU to "В настоящее время заняты",
            AppLanguage.TR to "Şu An Dolu / Konaklamada",
            AppLanguage.EN to "Currently Occupied"
        ),
        "Acenta Kontenjanı" to mapOf(
            AppLanguage.RU to "Квота агентств",
            AppLanguage.TR to "Acenta Kontenjanı",
            AppLanguage.EN to "Agency Allotment"
        ),
        "Acenta Tahsisleri" to mapOf(
            AppLanguage.RU to "Выделения агентствам",
            AppLanguage.TR to "Acenta Tahsisleri",
            AppLanguage.EN to "Agency Allocations"
        ),
        "Dönemsel doluluk trend analizi" to mapOf(
            AppLanguage.RU to "Анализ тренда загрузки",
            AppLanguage.TR to "Dönemsel doluluk trend analizi",
            AppLanguage.EN to "Periodic occupancy trend analysis"
        ),
        "Hafta" to mapOf(
            AppLanguage.RU to "Неделя",
            AppLanguage.TR to "Hafta",
            AppLanguage.EN to "Week"
        ),
        "Ay" to mapOf(
            AppLanguage.RU to "Месяц",
            AppLanguage.TR to "Ay",
            AppLanguage.EN to "Month"
        ),
        "Yıl" to mapOf(
            AppLanguage.RU to "Год",
            AppLanguage.TR to "Yıl",
            AppLanguage.EN to "Year"
        ),
        "Doluluk" to mapOf(
            AppLanguage.RU to "Загрузка",
            AppLanguage.TR to "Doluluk",
            AppLanguage.EN to "Occupancy"
        ),
        "Açık" to mapOf(
            AppLanguage.RU to "Активен",
            AppLanguage.TR to "Açık",
            AppLanguage.EN to "Active"
        ),
        "Engelli" to mapOf(
            AppLanguage.RU to "Заблокирован",
            AppLanguage.TR to "Engelli",
            AppLanguage.EN to "Blocked"
        ),
        "Üyelik Kodu" to mapOf(
            AppLanguage.RU to "Код участника",
            AppLanguage.TR to "Üyelik Kodu",
            AppLanguage.EN to "Member Code"
        ),
        "Üye Başlangıç" to mapOf(
            AppLanguage.RU to "Дата начала",
            AppLanguage.TR to "Üye Başlangıç",
            AppLanguage.EN to "Start Date"
        ),
        "Üye Bitiş" to mapOf(
            AppLanguage.RU to "Дата окончания",
            AppLanguage.TR to "Üye Bitiş",
            AppLanguage.EN to "End Date"
        ),
        "Telefon" to mapOf(
            AppLanguage.RU to "Телефон",
            AppLanguage.TR to "Telefon",
            AppLanguage.EN to "Phone"
        ),
        "e-mail" to mapOf(
            AppLanguage.RU to "Эл. почта",
            AppLanguage.TR to "e-mail",
            AppLanguage.EN to "Email"
        ),
        "Aktif Üye" to mapOf(
            AppLanguage.RU to "Активный участник",
            AppLanguage.TR to "Aktif Üye",
            AppLanguage.EN to "Active Member"
        ),
        "Pasif (Girişi Engelli)" to mapOf(
            AppLanguage.RU to "Неактивен (Доступ заблокирован)",
            AppLanguage.TR to "Pasif (Girişi Engelli)",
            AppLanguage.EN to "Inactive (Access Disabled)"
        ),
        "Agency Allotments (Today)" to mapOf(
            AppLanguage.RU to "Квоты агентств (на сегодня)",
            AppLanguage.TR to "Acente Kontenjanları (Bugün)",
            AppLanguage.EN to "Agency Allotments (Today)"
        ),
        "No allotments reserved for today." to mapOf(
            AppLanguage.RU to "На сегодня нет зарезервированных квот.",
            AppLanguage.TR to "Bugün için ayrılmış kontenjan bulunmuyor.",
            AppLanguage.EN to "No allotments reserved for today."
        ),
        "Unknown Agency" to mapOf(
            AppLanguage.RU to "Неизвестное агентство",
            AppLanguage.TR to "Bilinmeyen Acente",
            AppLanguage.EN to "Unknown Agency"
        ),
        "Kontenjan Blokajı" to mapOf(
            AppLanguage.RU to "Блокировка квоты",
            AppLanguage.TR to "Kontenjan Blokajı",
            AppLanguage.EN to "Allotment Block"
        ),
        "Hızlı Modül Erişimi" to mapOf(
            AppLanguage.RU to "Быстрый доступ к модулям",
            AppLanguage.TR to "Hızlı Modül Erişimi",
            AppLanguage.EN to "Quick Module Access"
        ),
        "Ön Büro Matrisi" to mapOf(
            AppLanguage.RU to "Матрица стойки регистрации",
            AppLanguage.TR to "Ön Büro Matrisi",
            AppLanguage.EN to "Front Desk Matrix"
        ),
        "Fiyatlandırma Yönetimi" to mapOf(
            AppLanguage.RU to "Управление ценами",
            AppLanguage.TR to "Fiyatlandırma Yönetimi",
            AppLanguage.EN to "Pricing Management"
        ),
        "Acente Yönetimi" to mapOf(
            AppLanguage.RU to "Управление агентствами",
            AppLanguage.TR to "Acente Yönetimi",
            AppLanguage.EN to "Agency Management"
        ),
        "Acente Sözleşmeleri" to mapOf(
            AppLanguage.RU to "Договоры с агентствами",
            AppLanguage.TR to "Acente Sözleşmeleri",
            AppLanguage.EN to "Agency Contracts"
        ),
        "Rezervasyon Listesi" to mapOf(
            AppLanguage.RU to "Список бронирований",
            AppLanguage.TR to "Rezervasyon Listesi",
            AppLanguage.EN to "Reservation List"
        ),
        "Çıkış Listesi" to mapOf(
            AppLanguage.RU to "Список выездов",
            AppLanguage.TR to "Çıkış Listesi",
            AppLanguage.EN to "Check-out List"
        ),
        "Giriş Listesi" to mapOf(
            AppLanguage.RU to "Список заездов",
            AppLanguage.TR to "Giriş Listesi",
            AppLanguage.EN to "Check-in List"
        ),
        "Bakım Onarım" to mapOf(
            AppLanguage.RU to "Техническое обслуживание",
            AppLanguage.TR to "Bakım Onarım",
            AppLanguage.EN to "Maintenance & Repair"
        ),
        "Kar & Zarar Raporu" to mapOf(
            AppLanguage.RU to "Отчет о прибылях и убытках",
            AppLanguage.TR to "Kar & Zarar Raporu",
            AppLanguage.EN to "Profit & Loss Report"
        ),
        "Acenta kartları, komisyon oranları ve acente kayıtları" to mapOf(
            AppLanguage.RU to "Карточки агентств, комиссии и реестр партнеров",
            AppLanguage.TR to "Acenta kartları, komisyon oranları ve acente kayıtları",
            AppLanguage.EN to "Agency cards, commission rates and agent registry"
        ),
        "Üye Yönetim Paneli" to mapOf(
            AppLanguage.RU to "Панель управления участниками",
            AppLanguage.TR to "Üye Yönetim Paneli",
            AppLanguage.EN to "Member Management Panel"
        ),
        "İsim, e-posta veya üye kodu ara..." to mapOf(
            AppLanguage.RU to "Поиск по имени, почте или коду...",
            AppLanguage.TR to "İsim, e-posta veya üye kodu ara...",
            AppLanguage.EN to "Search name, email or member code..."
        ),
        "Aramaya uygun üye bulunamadı." to mapOf(
            AppLanguage.RU to "Участники не найдены.",
            AppLanguage.TR to "Aramaya uygun üye bulunamadı.",
            AppLanguage.EN to "No matching members found."
        ),
        "Tekrar Dene" to mapOf(
            AppLanguage.RU to "Попробовать снова",
            AppLanguage.TR to "Tekrar Dene",
            AppLanguage.EN to "Try Again"
        ),
        "Oda Tiplerine Göre Müsaitlik Matrisi" to mapOf(
            AppLanguage.RU to "Матрица доступности по типам номеров",
            AppLanguage.TR to "Oda Tiplerine Göre Müsaitlik Matrisi",
            AppLanguage.EN to "Availability Matrix by Room Types"
        ),
        "14 Gün" to mapOf(
            AppLanguage.RU to "14 Дней",
            AppLanguage.TR to "14 Gün",
            AppLanguage.EN to "14 Days"
        ),
        "Bugün" to mapOf(
            AppLanguage.RU to "Сегодня",
            AppLanguage.TR to "Bugün",
            AppLanguage.EN to "Today"
        ),
        "Önceki" to mapOf(
            AppLanguage.RU to "Назад",
            AppLanguage.TR to "Önceki",
            AppLanguage.EN to "Previous"
        ),
        "Sonraki" to mapOf(
            AppLanguage.RU to "Вперед",
            AppLanguage.TR to "Sonraki",
            AppLanguage.EN to "Next"
        ),
        "Müsait (>2)" to mapOf(
            AppLanguage.RU to "Свободно (>2)",
            AppLanguage.TR to "Müsait (>2)",
            AppLanguage.EN to "Available (>2)"
        ),
        "Kritik / Az Kaldı (≤2)" to mapOf(
            AppLanguage.RU to "Критический остаток (≤2)",
            AppLanguage.TR to "Kritik / Az Kaldı (≤2)",
            AppLanguage.EN to "Low Stock (≤2)"
        ),
        "DOLU (0)" to mapOf(
            AppLanguage.RU to "НЕТ МЕСТ (0)",
            AppLanguage.TR to "DOLU (0)",
            AppLanguage.EN to "FULL (0)"
        ),
        "Oca" to mapOf(
            AppLanguage.RU to "Янв",
            AppLanguage.TR to "Oca",
            AppLanguage.EN to "Jan"
        ),
        "Şub" to mapOf(
            AppLanguage.RU to "Фев",
            AppLanguage.TR to "Şub",
            AppLanguage.EN to "Feb"
        ),
        "Mar" to mapOf(
            AppLanguage.RU to "Мар",
            AppLanguage.TR to "Mar",
            AppLanguage.EN to "Mar"
        ),
        "Nis" to mapOf(
            AppLanguage.RU to "Апр",
            AppLanguage.TR to "Nis",
            AppLanguage.EN to "Apr"
        ),
        "May" to mapOf(
            AppLanguage.RU to "Май",
            AppLanguage.TR to "May",
            AppLanguage.EN to "May"
        ),
        "Haz" to mapOf(
            AppLanguage.RU to "Июн",
            AppLanguage.TR to "Haz",
            AppLanguage.EN to "Jun"
        ),
        "Tem" to mapOf(
            AppLanguage.RU to "Июл",
            AppLanguage.TR to "Tem",
            AppLanguage.EN to "Jul"
        ),
        "Ağu" to mapOf(
            AppLanguage.RU to "Авг",
            AppLanguage.TR to "Ağu",
            AppLanguage.EN to "Aug"
        ),
        "Eyl" to mapOf(
            AppLanguage.RU to "Сен",
            AppLanguage.TR to "Eyl",
            AppLanguage.EN to "Sep"
        ),
        "Eki" to mapOf(
            AppLanguage.RU to "Окт",
            AppLanguage.TR to "Eki",
            AppLanguage.EN to "Oct"
        ),
        "Kas" to mapOf(
            AppLanguage.RU to "Ноя",
            AppLanguage.TR to "Kas",
            AppLanguage.EN to "Nov"
        ),
        "Ara" to mapOf(
            AppLanguage.RU to "Дек",
            AppLanguage.TR to "Ara",
            AppLanguage.EN to "Dec"
        ),
        "Pzt" to mapOf(
            AppLanguage.RU to "Пн",
            AppLanguage.TR to "Pzt",
            AppLanguage.EN to "Mon"
        ),
        "Sal" to mapOf(
            AppLanguage.RU to "Вт",
            AppLanguage.TR to "Sal",
            AppLanguage.EN to "Tue"
        ),
        "Çar" to mapOf(
            AppLanguage.RU to "Ср",
            AppLanguage.TR to "Çar",
            AppLanguage.EN to "Wed"
        ),
        "Per" to mapOf(
            AppLanguage.RU to "Чт",
            AppLanguage.TR to "Per",
            AppLanguage.EN to "Thu"
        ),
        "Cum" to mapOf(
            AppLanguage.RU to "Пт",
            AppLanguage.TR to "Cum",
            AppLanguage.EN to "Fri"
        ),
        "Cmt" to mapOf(
            AppLanguage.RU to "Сб",
            AppLanguage.TR to "Cmt",
            AppLanguage.EN to "Sat"
        ),
        "Paz" to mapOf(
            AppLanguage.RU to "Вс",
            AppLanguage.TR to "Paz",
            AppLanguage.EN to "Sun"
        ),
        "Room Rack" to mapOf(
            AppLanguage.RU to "Стойка номеров",
            AppLanguage.TR to "Oda Durumları (Rack)",
            AppLanguage.EN to "Room Rack"
        ),
        "Floor Plan & Occupancy Monitoring" to mapOf(
            AppLanguage.RU to "План этажей и мониторинг загрузки",
            AppLanguage.TR to "Kat Planı ve Doluluk Takibi",
            AppLanguage.EN to "Floor Plan & Occupancy Monitoring"
        ),
        "Back" to mapOf(
            AppLanguage.RU to "Назад",
            AppLanguage.TR to "Geri",
            AppLanguage.EN to "Back"
        ),
        "Kat Hizmetleri & Oda Temizliği" to mapOf(
            AppLanguage.RU to "Хозяйственная служба и уборка",
            AppLanguage.TR to "Kat Hizmetleri & Oda Temizliği",
            AppLanguage.EN to "Housekeeping & Room Cleaning"
        ),
        "Oda Durumları, Temizlik ve Minibar Takibi" to mapOf(
            AppLanguage.RU to "Статусы номеров, уборка и мини-бар",
            AppLanguage.TR to "Oda Durumları, Temizlik ve Minibar Takibi",
            AppLanguage.EN to "Room status, cleaning and minibar tracking"
        ),
        "Görev Ata" to mapOf(
            AppLanguage.RU to "Назначить задачу",
            AppLanguage.TR to "Görev Ata",
            AppLanguage.EN to "Assign Task"
        ),
        "Toplam Oda" to mapOf(
            AppLanguage.RU to "Всего номеров",
            AppLanguage.TR to "Toplam Oda",
            AppLanguage.EN to "Total Rooms"
        ),
        "Kirli Odalar" to mapOf(
            AppLanguage.RU to "Грязные номера",
            AppLanguage.TR to "Kirli Odalar",
            AppLanguage.EN to "Dirty Rooms"
        ),
        "Temizlikte" to mapOf(
            AppLanguage.RU to "В процессе уборки",
            AppLanguage.TR to "Temizlikte",
            AppLanguage.EN to "In Cleaning"
        ),
        "Temiz & Hazır" to mapOf(
            AppLanguage.RU to "Чистые и готовы",
            AppLanguage.TR to "Temiz & Hazır",
            AppLanguage.EN to "Clean & Ready"
        ),
        "Otel kadrosuna yeni çalışan tanımlayın" to mapOf(
            AppLanguage.RU to "Зарегистрируйте нового сотрудника в штате",
            AppLanguage.TR to "Otel kadrosuna yeni çalışan tanımlayın",
            AppLanguage.EN to "Register new employee in hotel staff"
        ),
        "Adı Soyadı" to mapOf(
            AppLanguage.RU to "Имя Фамилия",
            AppLanguage.TR to "Adı Soyadı",
            AppLanguage.EN to "Full Name"
        ),
        "Departman" to mapOf(
            AppLanguage.RU to "Отдел / Департамент",
            AppLanguage.TR to "Departman",
            AppLanguage.EN to "Department"
        ),
        "Departman seçiniz..." to mapOf(
            AppLanguage.RU to "Выберите отдел...",
            AppLanguage.TR to "Departman seçiniz...",
            AppLanguage.EN to "Select department..."
        ),
        "Tanımlı departman bulunamadı" to mapOf(
            AppLanguage.RU to "Отделы не найдены",
            AppLanguage.TR to "Tanımlı departman bulunamadı",
            AppLanguage.EN to "No departments found"
        ),
        "Tarih" to mapOf(
            AppLanguage.RU to "Дата",
            AppLanguage.TR to "Tarih",
            AppLanguage.EN to "Date"
        ),
        "Kirlet" to mapOf(
            AppLanguage.RU to "Сделать грязным",
            AppLanguage.TR to "Kirlet",
            AppLanguage.EN to "Mark Dirty"
        ),
        "Başlat" to mapOf(
            AppLanguage.RU to "Начать",
            AppLanguage.TR to "Başlat",
            AppLanguage.EN to "Start"
        ),
        "Temizlendi" to mapOf(
            AppLanguage.RU to "Убрано",
            AppLanguage.TR to "Temizlendi",
            AppLanguage.EN to "Cleaned"
        ),
        "Temiz (Hazır)" to mapOf(
            AppLanguage.RU to "Чистый (Готов к заселению)",
            AppLanguage.TR to "Temiz (Hazır)",
            AppLanguage.EN to "Clean (Ready)"
        ),
        "Kirli (Temizlenecek)" to mapOf(
            AppLanguage.RU to "Грязный (Требуется уборка)",
            AppLanguage.TR to "Kirli (Temizlenecek)",
            AppLanguage.EN to "Dirty (To be cleaned)"
        ),
        "Temizlik Sürüyor" to mapOf(
            AppLanguage.RU to "Идет уборка",
            AppLanguage.TR to "Temizlik Sürüyor",
            AppLanguage.EN to "Cleaning in Progress"
        ),
        "Oda no ile ara (Örn: 104)..." to mapOf(
            AppLanguage.RU to "Поиск по номеру (напр. 104)...",
            AppLanguage.TR to "Oda no ile ara (Örn: 104)...",
            AppLanguage.EN to "Search by room no (e.g. 104)..."
        ),
        "Oda no ile ara (Örn: 104)" to mapOf(
            AppLanguage.RU to "Поиск по номеру (напр. 104)",
            AppLanguage.TR to "Oda no ile ara (Örn: 104)",
            AppLanguage.EN to "Search by room no (e.g. 104)"
        ),
        "Seçilen filtreye uygun oda bulunamadı." to mapOf(
            AppLanguage.RU to "Номеров по выбранному фильтру не найдено.",
            AppLanguage.TR to "Seçilen filtreye uygun oda bulunamadı.",
            AppLanguage.EN to "No rooms found for selected filter."
        ),
        "İşlem Seçin" to mapOf(
            AppLanguage.RU to "Выберите действие",
            AppLanguage.TR to "İşlem Seçin",
            AppLanguage.EN to "Select Action"
        ),
        "Bu oda için gerçekleştirmek istediğiniz işlemi seçiniz:" to mapOf(
            AppLanguage.RU to "Выберите действие для этого номера:",
            AppLanguage.TR to "Bu oda için gerçekleştirmek istediğiniz işlemi seçiniz:",
            AppLanguage.EN to "Select the action you want to perform for this room:"
        ),
        "Temizliği Başlat (İşlemde)" to mapOf(
            AppLanguage.RU to "Начать уборку (В процессе)",
            AppLanguage.TR to "Temizliği Başlat (İşlemde)",
            AppLanguage.EN to "Start Cleaning (In Progress)"
        ),
        "Temizlendi (Malzeme Düşerek)" to mapOf(
            AppLanguage.RU to "Убран (Списать материалы)",
            AppLanguage.TR to "Temizlendi (Malzeme Düşerek)",
            AppLanguage.EN to "Cleaned (Deduct Supplies)"
        ),
        "Minibar Harcaması Ekle" to mapOf(
            AppLanguage.RU to "Добавить расход мини-бара",
            AppLanguage.TR to "Minibar Harcaması Ekle",
            AppLanguage.EN to "Add Minibar Expense"
        ),
        "Arıza & Bakım Bildir" to mapOf(
            AppLanguage.RU to "Сообщить о неисправности",
            AppLanguage.TR to "Arıza & Bakım Bildir",
            AppLanguage.EN to "Report Issue & Maintenance"
        ),
        "Mevcut Stok:" to mapOf(
            AppLanguage.RU to "Текущий остаток:",
            AppLanguage.TR to "Mevcut Stok:",
            AppLanguage.EN to "Current Stock:"
        ),
        "Temizliği Tamamla & Stok Düş" to mapOf(
            AppLanguage.RU to "Завершить уборку и списать",
            AppLanguage.TR to "Temizliği Tamamla & Stok Düş",
            AppLanguage.EN to "Finish Cleaning & Deduct Stock"
        ),
        "Minibar Tüketimi" to mapOf(
            AppLanguage.RU to "Расход мини-бара",
            AppLanguage.TR to "Minibar Tüketimi",
            AppLanguage.EN to "Minibar Consumption"
        ),
        "Tüketilen minibar ürünlerini seçin. Tutar oda folyosuna işlenecektir." to mapOf(
            AppLanguage.RU to "Выберите товары мини-бара. Сумма будет добавлена на счет номера.",
            AppLanguage.TR to "Tüketilen minibar ürünlerini seçin. Tutar oda folyosuna işlenecektir.",
            AppLanguage.EN to "Select consumed minibar items. Amount will be added to room folio."
        ),
        "Toplam Minibar Tutarı:" to mapOf(
            AppLanguage.RU to "Итого по мини-бару:",
            AppLanguage.TR to "Toplam Minibar Tutarı:",
            AppLanguage.EN to "Total Minibar Amount:"
        ),
        "Oda Hesabına Yaz" to mapOf(
            AppLanguage.RU to "Записать на номер",
            AppLanguage.TR to "Oda Hesabına Yaz",
            AppLanguage.EN to "Charge to Room"
        ),
        "Arıza Bildir" to mapOf(
            AppLanguage.RU to "Сообщить о поломке",
            AppLanguage.TR to "Arıza Bildir",
            AppLanguage.EN to "Report Breakdown"
        ),
        "Bu oda için tespit edilen arıza detayını yazın. Teknik servise iletilecek ve oda bakım durumuna alınacaktır." to mapOf(
            AppLanguage.RU to "Опишите неисправность. Заявка будет передана в техслужбу, номер перейдет на обслуживание.",
            AppLanguage.TR to "Bu oda için tespit edilen arıza detayını yazın. Teknik servise iletilecek ve oda bakım durumuna alınacaktır.",
            AppLanguage.EN to "Describe the issue. It will be sent to tech service and the room will be set to maintenance."
        ),
        "Örn: Klima soğutmuyor veya banyo musluğu damlatıyor..." to mapOf(
            AppLanguage.RU to "Напр: Не работает кондиционер или течет кран...",
            AppLanguage.TR to "Örn: Klima soğutmuyor veya banyo musluğu damlatıyor...",
            AppLanguage.EN to "E.g. AC not cooling or bathroom tap leaking..."
        ),
        "Arızayı Kaydet" to mapOf(
            AppLanguage.RU to "Зафиксировать поломку",
            AppLanguage.TR to "Arızayı Kaydet",
            AppLanguage.EN to "Save Issue"
        ),
        "Yeni Temizlik Görevi Planla" to mapOf(
            AppLanguage.RU to "Запланировать уборку",
            AppLanguage.TR to "Yeni Temizlik Görevi Planla",
            AppLanguage.EN to "Plan Cleaning Task"
        ),
        "Kat görevlisi atamaları ve otomatik temizlik görevleri sistem tarafından oda check-in / check-out durumlarına göre senkronize edilmektedir." to mapOf(
            AppLanguage.RU to "Назначения горничных и задачи уборки синхронизируются системой по статусам заездов и выездов.",
            AppLanguage.TR to "Kat görevlisi atamaları ve otomatik temizlik görevleri sistem tarafından oda check-in / check-out durumlarına göre senkronize edilmektedir.",
            AppLanguage.EN to "Housekeeper assignments and cleaning tasks are synced by the system according to check-in/out statuses."
        ),
        "Anladım" to mapOf(
            AppLanguage.RU to "Понятно",
            AppLanguage.TR to "Anladım",
            AppLanguage.EN to "Got it"
        ),
        "Nakit ve Banka Hareketleri Takibi" to mapOf(
            AppLanguage.RU to "Учет наличных и банковских операций",
            AppLanguage.TR to "Nakit ve Banka Hareketleri Takibi",
            AppLanguage.EN to "Cash & Bank Transactions Tracking"
        ),
        "Yeni İşlem" to mapOf(
            AppLanguage.RU to "+ Новая операция",
            AppLanguage.TR to "Yeni İşlem",
            AppLanguage.EN to "New Transaction"
        ),
        "Kasa Bakiyesi" to mapOf(
            AppLanguage.RU to "Остаток в кассе",
            AppLanguage.TR to "Kasa Bakiyesi",
            AppLanguage.EN to "Cash Balance"
        ),
        "Net" to mapOf(
            AppLanguage.RU to "Чистый",
            AppLanguage.TR to "Net",
            AppLanguage.EN to "Net"
        ),
        "Toplam Gelir" to mapOf(
            AppLanguage.RU to "Общий доход",
            AppLanguage.TR to "Toplam Gelir",
            AppLanguage.EN to "Total Income"
        ),
        "Toplam Gider" to mapOf(
            AppLanguage.RU to "Общий расход",
            AppLanguage.TR to "Toplam Gider",
            AppLanguage.EN to "Total Expense"
        ),
        "İşlem ara..." to mapOf(
            AppLanguage.RU to "Поиск операции...",
            AppLanguage.TR to "İşlem ara...",
            AppLanguage.EN to "Search transaction..."
        ),
        "Gelirler" to mapOf(
            AppLanguage.RU to "Доходы",
            AppLanguage.TR to "Gelirler",
            AppLanguage.EN to "Incomes"
        ),
        "Giderler" to mapOf(
            AppLanguage.RU to "Расходы",
            AppLanguage.TR to "Giderler",
            AppLanguage.EN to "Expenses"
        ),
        "Son Kasa Hareketleri" to mapOf(
            AppLanguage.RU to "Последние кассовые операции",
            AppLanguage.TR to "Son Kasa Hareketleri",
            AppLanguage.EN to "Recent Cash Transactions"
        ),
        "Kasa hareketi bulunamadı" to mapOf(
            AppLanguage.RU to "Кассовых операций не найдено",
            AppLanguage.TR to "Kasa hareketi bulunamadı",
            AppLanguage.EN to "No cash movements found"
        ),
        "Gelir veya gider kaydetmek için 'Yeni İşlem' butonunu kullanabilirsiniz." to mapOf(
            AppLanguage.RU to "Используйте кнопку 'Новая операция' для добавления доходов или расходов.",
            AppLanguage.TR to "Gelir veya gider kaydetmek için 'Yeni İşlem' butonunu kullanabilirsiniz.",
            AppLanguage.EN to "You can use the 'New Transaction' button to record income or expense."
        ),
        "Yeni İşlem Ekle" to mapOf(
            AppLanguage.RU to "Добавить новую операцию",
            AppLanguage.TR to "Yeni İşlem Ekle",
            AppLanguage.EN to "Add New Transaction"
        ),
        "Gelir Girişi" to mapOf(
            AppLanguage.RU to "Внесение дохода",
            AppLanguage.TR to "Gelir Girişi",
            AppLanguage.EN to "Income Entry"
        ),
        "Gider Çıkışı" to mapOf(
            AppLanguage.RU to "Списание расхода",
            AppLanguage.TR to "Gider Çıkışı",
            AppLanguage.EN to "Expense Entry"
        ),
        "Tutar" to mapOf(
            AppLanguage.RU to "Сумма",
            AppLanguage.TR to "Tutar",
            AppLanguage.EN to "Amount"
        ),
        "Açıklama (Opsiyonel)" to mapOf(
            AppLanguage.RU to "Примечание (необязательно)",
            AppLanguage.TR to "Açıklama (Opsiyonel)",
            AppLanguage.EN to "Notes (Optional)"
        ),
        "İşlemi Kaydet" to mapOf(
            AppLanguage.RU to "Сохранить операцию",
            AppLanguage.TR to "İşlemi Kaydet",
            AppLanguage.EN to "Save Transaction"
        ),
        "Konaklama Geliri" to mapOf(
            AppLanguage.RU to "Доход от проживания",
            AppLanguage.TR to "Konaklama Geliri",
            AppLanguage.EN to "Accommodation Income"
        ),
        "Restoran Geliri" to mapOf(
            AppLanguage.RU to "Доход ресторана",
            AppLanguage.TR to "Restoran Geliri",
            AppLanguage.EN to "Restaurant Income"
        ),
        "Restoran / Kafe" to mapOf(
            AppLanguage.RU to "Ресторан / Кафе",
            AppLanguage.TR to "Restoran / Kafe",
            AppLanguage.EN to "Restaurant / Cafe"
        ),
        "Ekstra Hizmetler" to mapOf(
            AppLanguage.RU to "Дополнительные услуги",
            AppLanguage.TR to "Ekstra Hizmetler",
            AppLanguage.EN to "Extra Services"
        ),
        "Tur & Aktivite" to mapOf(
            AppLanguage.RU to "Экскурсии и активность",
            AppLanguage.TR to "Tur & Aktivite",
            AppLanguage.EN to "Tours & Activities"
        ),
        "Diğer Gelir" to mapOf(
            AppLanguage.RU to "Прочий доход",
            AppLanguage.TR to "Diğer Gelir",
            AppLanguage.EN to "Other Income"
        ),
        "Maaş / Personel" to mapOf(
            AppLanguage.RU to "Зарплата / Персонал",
            AppLanguage.TR to "Maaş / Personel",
            AppLanguage.EN to "Salary / Staff"
        ),
        "Fatura (Elektrik/Su/İnternet)" to mapOf(
            AppLanguage.RU to "Коммунальные счета (Электричество/Вода/Интернет)",
            AppLanguage.TR to "Fatura (Elektrik/Su/İnternet)",
            AppLanguage.EN to "Bill (Electricity/Water/Internet)"
        ),
        "Mutfak / Gıda Alımı" to mapOf(
            AppLanguage.RU to "Кухня / Закупка продуктов",
            AppLanguage.TR to "Mutfak / Gıda Alımı",
            AppLanguage.EN to "Kitchen / Food Purchases"
        ),
        "Temizlik Malzemeleri" to mapOf(
            AppLanguage.RU to "Средства для уборки",
            AppLanguage.TR to "Temizlik Malzemeleri",
            AppLanguage.EN to "Cleaning Supplies"
        ),
        "Bakım / Onarım" to mapOf(
            AppLanguage.RU to "Обслуживание / Ремонт",
            AppLanguage.TR to "Bakım / Onarım",
            AppLanguage.EN to "Maintenance / Repair"
        ),
        "Pazarlama / Reklam" to mapOf(
            AppLanguage.RU to "Маркетинг / Реклама",
            AppLanguage.TR to "Pazarlama / Reklam",
            AppLanguage.EN to "Marketing / Ads"
        ),
        "Diğer Gider" to mapOf(
            AppLanguage.RU to "Прочий расход",
            AppLanguage.TR to "Diğer Gider",
            AppLanguage.EN to "Other Expense"
        ),
        "Kasa İşlemi" to mapOf(
            AppLanguage.RU to "Кассовая операция",
            AppLanguage.TR to "Kasa İşlemi",
            AppLanguage.EN to "Cash Transaction"
        ),
        "Restoran POS & Adisyon" to mapOf(
            AppLanguage.RU to "POS Ресторан и заказы",
            AppLanguage.TR to "Restoran POS & Adisyon",
            AppLanguage.EN to "Restaurant POS & Bills"
        ),
        "Masa siparişleri ve anlık adisyon yönetimi" to mapOf(
            AppLanguage.RU to "Заказы столов и управление счетами",
            AppLanguage.TR to "Masa siparişleri ve anlık adisyon yönetimi",
            AppLanguage.EN to "Table orders and live billing management"
        ),
        "Toplam Adisyon Tutarı" to mapOf(
            AppLanguage.RU to "Общая сумма заказов",
            AppLanguage.TR to "Toplam Adisyon Tutarı",
            AppLanguage.EN to "Total Orders Amount"
        ),
        "+ Yeni Masa Aç" to mapOf(
            AppLanguage.RU to "+ Открыть новый стол",
            AppLanguage.TR to "+ Yeni Masa Aç",
            AppLanguage.EN to "+ Open New Table"
        ),
        "Adisyon başlatmak için masa seçin" to mapOf(
            AppLanguage.RU to "Выберите стол для открытия счета",
            AppLanguage.TR to "Adisyon başlatmak için masa seçin",
            AppLanguage.EN to "Select table to open bill"
        ),
        "Hızlı Masa Seçenekleri" to mapOf(
            AppLanguage.RU to "Быстрый выбор стола",
            AppLanguage.TR to "Hızlı Masa Seçenekleri",
            AppLanguage.EN to "Quick Table Options"
        ),
        "Masa Aç & Adisyona Git" to mapOf(
            AppLanguage.RU to "Открыть стол и перейти к заказу",
            AppLanguage.TR to "Masa Aç & Adisyona Git",
            AppLanguage.EN to "Open Table & Go to Bill"
        ),
        "+ Masa Aç & Adisyona Git" to mapOf(
            AppLanguage.RU to "+ Открыть стол и перейти к заказу",
            AppLanguage.TR to "+ Masa Aç & Adisyona Git",
            AppLanguage.EN to "+ Open Table & Go to Bill"
        ),
        "Adisyon Tutarı" to mapOf(
            AppLanguage.RU to "Сумма заказа",
            AppLanguage.TR to "Adisyon Tutarı",
            AppLanguage.EN to "Order Amount"
        ),
        "Masa" to mapOf(
            AppLanguage.RU to "Стол",
            AppLanguage.TR to "Masa",
            AppLanguage.EN to "Table"
        ),
        "Bahçe" to mapOf(
            AppLanguage.RU to "Сад",
            AppLanguage.TR to "Bahçe",
            AppLanguage.EN to "Garden"
        ),
        "Teras" to mapOf(
            AppLanguage.RU to "Терраса",
            AppLanguage.TR to "Teras",
            AppLanguage.EN to "Terrace"
        ),
        "Yeni Yemek / Ürün Ekle" to mapOf(
            AppLanguage.RU to "Добавить новое блюдо / товар",
            AppLanguage.TR to "Yeni Yemek / Ürün Ekle",
            AppLanguage.EN to "Add New Dish / Item"
        ),
        "Yemek / Ürün Adı" to mapOf(
            AppLanguage.RU to "Название блюда / товара",
            AppLanguage.TR to "Yemek / Ürün Adı",
            AppLanguage.EN to "Dish / Item Name"
        ),
        "Kategori Seçin:" to mapOf(
            AppLanguage.RU to "Выберите категорию:",
            AppLanguage.TR to "Kategori Seçin:",
            AppLanguage.EN to "Select Category:"
        ),
        "Satışta / Mevcut" to mapOf(
            AppLanguage.RU to "В продаже / Доступно",
            AppLanguage.TR to "Satışta / Mevcut",
            AppLanguage.EN to "Available for Sale"
        ),
        "Menüye Ekle" to mapOf(
            AppLanguage.RU to "Добавить в меню",
            AppLanguage.TR to "Menüye Ekle",
            AppLanguage.EN to "Add to Menu"
        ),
        "Kayıtlı Acenta" to mapOf(
            AppLanguage.RU to "Зарегистрированных агентств",
            AppLanguage.TR to "Kayıtlı Acenta",
            AppLanguage.EN to "Registered Agencies"
        ),
        "Sözleşmeler" to mapOf(
            AppLanguage.RU to "Договоры",
            AppLanguage.TR to "Sözleşmeler",
            AppLanguage.EN to "Contracts"
        ),
        "Yeni Acenta Ekle" to mapOf(
            AppLanguage.RU to "Добавить новое агентство",
            AppLanguage.TR to "Yeni Acenta Ekle",
            AppLanguage.EN to "Add New Agency"
        ),
        "İletişim / E-posta / Telefon" to mapOf(
            AppLanguage.RU to "Контакты / Эл. почта / Телефон",
            AppLanguage.TR to "İletişim / E-posta / Telefon",
            AppLanguage.EN to "Contact / Email / Phone"
        ),
        "Komisyon Oranı" to mapOf(
            AppLanguage.RU to "Ставка комиссии",
            AppLanguage.TR to "Komisyon Oranı",
            AppLanguage.EN to "Commission Rate"
        ),
        "Komisyon Oranı (%)" to mapOf(
            AppLanguage.RU to "Ставка комиссии (%)",
            AppLanguage.TR to "Komisyon Oranı (%)",
            AppLanguage.EN to "Commission Rate (%)"
        ),
        "Henüz acenta kaydı bulunmuyor" to mapOf(
            AppLanguage.RU to "Агентств пока нет",
            AppLanguage.TR to "Henüz acenta kaydı bulunmuyor",
            AppLanguage.EN to "No agencies registered yet"
        ),
        "Acentayı Sil" to mapOf(
            AppLanguage.RU to "Удалить агентство",
            AppLanguage.TR to "Acentayı Sil",
            AppLanguage.EN to "Delete Agency"
        ),
        "Ocak" to mapOf(
            AppLanguage.RU to "Январь",
            AppLanguage.TR to "Ocak",
            AppLanguage.EN to "January"
        ),
        "Şubat" to mapOf(
            AppLanguage.RU to "Февраль",
            AppLanguage.TR to "Şubat",
            AppLanguage.EN to "February"
        ),
        "Mart" to mapOf(
            AppLanguage.RU to "Март",
            AppLanguage.TR to "Mart",
            AppLanguage.EN to "March"
        ),
        "Nisan" to mapOf(
            AppLanguage.RU to "Апрель",
            AppLanguage.TR to "Nisan",
            AppLanguage.EN to "April"
        ),
        "Mayıs" to mapOf(
            AppLanguage.RU to "Май",
            AppLanguage.TR to "Mayıs",
            AppLanguage.EN to "May"
        ),
        "Haziran" to mapOf(
            AppLanguage.RU to "Июнь",
            AppLanguage.TR to "Haziran",
            AppLanguage.EN to "June"
        ),
        "Temmuz" to mapOf(
            AppLanguage.RU to "Июль",
            AppLanguage.TR to "Temmuz",
            AppLanguage.EN to "July"
        ),
        "Ağustos" to mapOf(
            AppLanguage.RU to "Август",
            AppLanguage.TR to "Ağustos",
            AppLanguage.EN to "August"
        ),
        "Eylül" to mapOf(
            AppLanguage.RU to "Сентябрь",
            AppLanguage.TR to "Eylül",
            AppLanguage.EN to "September"
        ),
        "Ekim" to mapOf(
            AppLanguage.RU to "Октябрь",
            AppLanguage.TR to "Ekim",
            AppLanguage.EN to "October"
        ),
        "Kasım" to mapOf(
            AppLanguage.RU to "Ноябрь",
            AppLanguage.TR to "Kasım",
            AppLanguage.EN to "November"
        ),
        "Aralık" to mapOf(
            AppLanguage.RU to "Декабрь",
            AppLanguage.TR to "Aralık",
            AppLanguage.EN to "December"
        ),

        "E-posta adresiniz" to mapOf(
            AppLanguage.RU to "Электронная почта",
            AppLanguage.TR to "E-posta adresiniz",
            AppLanguage.EN to "E-posta adresiniz"
        ),
        "Şifreniz" to mapOf(
            AppLanguage.RU to "Пароль",
            AppLanguage.TR to "Şifreniz",
            AppLanguage.EN to "Şifreniz"
        ),
        "Üye Kodu (Örn: MBR-A8X92)" to mapOf(
            AppLanguage.RU to "Код участника (напр. MBR-ADMIN01)",
            AppLanguage.TR to "Üye Kodu (Örn: MBR-A8X92)",
            AppLanguage.EN to "Üye Kodu (Örn: MBR-A8X92)"
        ),
        "Üye Kodu (Örn: MBR-ADMIN01 / DEMO)" to mapOf(
            AppLanguage.RU to "Код участника (напр. MBR-ADMIN01 / DEMO)",
            AppLanguage.TR to "Üye Kodu (Örn: MBR-ADMIN01 / DEMO)",
            AppLanguage.EN to "Üye Kodu (Örn: MBR-ADMIN01 / DEMO)"
        ),
        "Admin tarafından tanımlanan benzersiz üyelik kodu" to mapOf(
            AppLanguage.RU to "Код участника, присвоенный администратором",
            AppLanguage.TR to "Admin tarafından tanımlanan benzersiz üyelik kodu",
            AppLanguage.EN to "Admin tarafından tanımlanan benzersiz üyelik kodu"
        ),
        "Admin tarafından tanımlanan benzersiz üyelik kodu veya DEMO" to mapOf(
            AppLanguage.RU to "Код участника, присвоенный администратором, или DEMO",
            AppLanguage.TR to "Admin tarafından tanımlanan benzersiz üyelik kodu veya DEMO",
            AppLanguage.EN to "Admin tarafından tanımlanan benzersiz üyelik kodu veya DEMO"
        ),
        "Beni hatırla" to mapOf(
            AppLanguage.RU to "Запомнить меня",
            AppLanguage.TR to "Beni hatırla",
            AppLanguage.EN to "Beni hatırla"
        ),
        "Şifremi Unuttum?" to mapOf(
            AppLanguage.RU to "Забыли пароль?",
            AppLanguage.TR to "Şifremi Unuttum?",
            AppLanguage.EN to "Şifremi Unuttum?"
        ),
        "veya" to mapOf(
            AppLanguage.RU to "или",
            AppLanguage.TR to "veya",
            AppLanguage.EN to "veya"
        ),
        "Demo Hesapla Giriş (test@hotelos.com)" to mapOf(
            AppLanguage.RU to "Демо-вход (test@hotelos.com)",
            AppLanguage.TR to "Demo Hesapla Giriş (test@hotelos.com)",
            AppLanguage.EN to "Demo Hesapla Giriş (test@hotelos.com)"
        ),
        "Güvenli Giriş" to mapOf(
            AppLanguage.RU to "Безопасный вход",
            AppLanguage.TR to "Güvenli Giriş",
            AppLanguage.EN to "Güvenli Giriş"
        ),
        "Hesabınız yok mu?" to mapOf(
            AppLanguage.RU to "Нет учетной записи?",
            AppLanguage.TR to "Hesabınız yok mu?",
            AppLanguage.EN to "Hesabınız yok mu?"
        ),
        "Güvenli" to mapOf(
            AppLanguage.RU to "Безопасность",
            AppLanguage.TR to "Güvenli",
            AppLanguage.EN to "Güvenli"
        ),
        "7/24 Erişim" to mapOf(
            AppLanguage.RU to "Доступ 24/7",
            AppLanguage.TR to "7/24 Erişim",
            AppLanguage.EN to "7/24 Erişim"
        ),
        "Raporlama" to mapOf(
            AppLanguage.RU to "Аналитика",
            AppLanguage.TR to "Raporlama",
            AppLanguage.EN to "Raporlama"
        ),
        "Otel Kaydı" to mapOf(
            AppLanguage.RU to "Регистрация отеля",
            AppLanguage.TR to "Otel Kaydı",
            AppLanguage.EN to "Otel Kaydı"
        ),
        "Yeni Otel Hesabı Oluşturun" to mapOf(
            AppLanguage.RU to "Создать аккаунт нового объекта размещения",
            AppLanguage.TR to "Yeni Otel Hesabı Oluşturun",
            AppLanguage.EN to "Yeni Otel Hesabı Oluşturun"
        ),
        "Şifre Tekrar" to mapOf(
            AppLanguage.RU to "Повтор пароля",
            AppLanguage.TR to "Şifre Tekrar",
            AppLanguage.EN to "Şifre Tekrar"
        ),
        "Zaten hesabınız var mı?" to mapOf(
            AppLanguage.RU to "Уже зарегистрированы?",
            AppLanguage.TR to "Zaten hesabınız var mı?",
            AppLanguage.EN to "Zaten hesabınız var mı?"
        ),
        "Giriş Yapın" to mapOf(
            AppLanguage.RU to "Войти",
            AppLanguage.TR to "Giriş Yapın",
            AppLanguage.EN to "Giriş Yapın"
        ),
        "E-posta adresinize sıfırlama bağlantısı gönderilecektir" to mapOf(
            AppLanguage.RU to "Инструкции по сбросу пароля будут отправлены на почту",
            AppLanguage.TR to "E-posta adresinize sıfırlama bağlantısı gönderilecektir",
            AppLanguage.EN to "E-posta adresinize sıfırlama bağlantısı gönderilecektir"
        ),
        "Geçerli bir e-posta adresi giriniz." to mapOf(
            AppLanguage.RU to "Введите корректный адрес эл. почты.",
            AppLanguage.TR to "Geçerli bir e-posta adresi giriniz.",
            AppLanguage.EN to "Geçerli bir e-posta adresi giriniz."
        ),
        "Şifreniz en az 6 karakter olmalıdır." to mapOf(
            AppLanguage.RU to "Пароль должен содержать не менее 6 символов.",
            AppLanguage.TR to "Şifreniz en az 6 karakter olmalıdır.",
            AppLanguage.EN to "Şifreniz en az 6 karakter olmalıdır."
        ),
        "Lütfen admin tarafından tanımlanan üye kodunuzu giriniz." to mapOf(
            AppLanguage.RU to "Пожалуйста, введите ваш код участника.",
            AppLanguage.TR to "Lütfen admin tarafından tanımlanan üye kodunuzu giriniz.",
            AppLanguage.EN to "Lütfen admin tarafından tanımlanan üye kodunuzu giriniz."
        ),
        "Demo şifresi '123456' olmalıdır." to mapOf(
            AppLanguage.RU to "Пароль для демо-аккаунта: 123456",
            AppLanguage.TR to "Demo şifresi '123456' olmalıdır.",
            AppLanguage.EN to "Demo şifresi '123456' olmalıdır."
        ),
        "Calendar" to mapOf(
            AppLanguage.RU to "Шахматка бронирования",
            AppLanguage.TR to "Calendar",
            AppLanguage.EN to "Calendar"
        ),
        "Change room" to mapOf(
            AppLanguage.RU to "Переселить в другой номер",
            AppLanguage.TR to "Change room",
            AppLanguage.EN to "Change room"
        ),
        "Direkt Check-In Yap (Rezervasyonsuz)" to mapOf(
            AppLanguage.RU to "Прямой заезд (от стойки / Walk-in)",
            AppLanguage.TR to "Direkt Check-In Yap (Rezervasyonsuz)",
            AppLanguage.EN to "Direkt Check-In Yap (Rezervasyonsuz)"
        ),
        "Ad Soyad" to mapOf(
            AppLanguage.RU to "ФИО гостя",
            AppLanguage.TR to "Ad Soyad",
            AppLanguage.EN to "Ad Soyad"
        ),
        "Doğum Tarihi" to mapOf(
            AppLanguage.RU to "Дата рождения",
            AppLanguage.TR to "Doğum Tarihi",
            AppLanguage.EN to "Doğum Tarihi"
        ),
        "Doğum Tarihi (GG.AA.YYYY)" to mapOf(
            AppLanguage.RU to "Дата рождения (ДД.ММ.ГГГГ)",
            AppLanguage.TR to "Doğum Tarihi (GG.AA.YYYY)",
            AppLanguage.EN to "Doğum Tarihi (GG.AA.YYYY)"
        ),
        "E-KBS Sistemine Gönder" to mapOf(
            AppLanguage.RU to "Передать данные в миграционный учет (МВД)",
            AppLanguage.TR to "E-KBS Sistemine Gönder",
            AppLanguage.EN to "E-KBS Sistemine Gönder"
        ),
        "Ekstra Hizmetler:" to mapOf(
            AppLanguage.RU to "Дополнительные услуги фолио:",
            AppLanguage.TR to "Ekstra Hizmetler:",
            AppLanguage.EN to "Ekstra Hizmetler:"
        ),
        "Genel Toplam" to mapOf(
            AppLanguage.RU to "Итого к оплате",
            AppLanguage.TR to "Genel Toplam",
            AppLanguage.EN to "Genel Toplam"
        ),
        "Genel Toplam: $250" to mapOf(
            AppLanguage.RU to "Общий итог к оплате",
            AppLanguage.TR to "Genel Toplam: $250",
            AppLanguage.EN to "Genel Toplam: $250"
        ),
        "Misafir Girişi (Check-in)" to mapOf(
            AppLanguage.RU to "Регистрация заезда (Check-in)",
            AppLanguage.TR to "Misafir Girişi (Check-in)",
            AppLanguage.EN to "Misafir Girişi (Check-in)"
        ),
        "Misafir Çıkışı (Check-out)" to mapOf(
            AppLanguage.RU to "Оформление выезда (Check-out)",
            AppLanguage.TR to "Misafir Çıkışı (Check-out)",
            AppLanguage.EN to "Misafir Çıkışı (Check-out)"
        ),
        "Giriş Bekleyenler" to mapOf(
            AppLanguage.RU to "Ожидаемые заезды на сегодня",
            AppLanguage.TR to "Giriş Bekleyenler",
            AppLanguage.EN to "Giriş Bekleyenler"
        ),
        "Çıkış Bekleyenler" to mapOf(
            AppLanguage.RU to "Ожидаемые выезды на сегодня",
            AppLanguage.TR to "Çıkış Bekleyenler",
            AppLanguage.EN to "Çıkış Bekleyenler"
        ),
        "Rezervasyon Detayı" to mapOf(
            AppLanguage.RU to "Детали бронирования",
            AppLanguage.TR to "Rezervasyon Detayı",
            AppLanguage.EN to "Rezervasyon Detayı"
        ),
        "Oda No" to mapOf(
            AppLanguage.RU to "Номер комнаты",
            AppLanguage.TR to "Oda No",
            AppLanguage.EN to "Oda No"
        ),
        "Giriş Tarihi" to mapOf(
            AppLanguage.RU to "Дата заезда",
            AppLanguage.TR to "Giriş Tarihi",
            AppLanguage.EN to "Giriş Tarihi"
        ),
        "Çıkış Tarihi" to mapOf(
            AppLanguage.RU to "Дата выезда",
            AppLanguage.TR to "Çıkış Tarihi",
            AppLanguage.EN to "Çıkış Tarihi"
        ),
        "Yetişkin Sayısı" to mapOf(
            AppLanguage.RU to "Количество взрослых",
            AppLanguage.TR to "Yetişkin Sayısı",
            AppLanguage.EN to "Yetişkin Sayısı"
        ),
        "Çocuk Sayısı" to mapOf(
            AppLanguage.RU to "Количество детей",
            AppLanguage.TR to "Çocuk Sayısı",
            AppLanguage.EN to "Çocuk Sayısı"
        ),
        "Özel Notlar" to mapOf(
            AppLanguage.RU to "Особые пожелания / Примечания",
            AppLanguage.TR to "Özel Notlar",
            AppLanguage.EN to "Özel Notlar"
        ),
        "Rezervasyonu Kaydet" to mapOf(
            AppLanguage.RU to "Сохранить бронирование",
            AppLanguage.TR to "Rezervasyonu Kaydet",
            AppLanguage.EN to "Rezervasyonu Kaydet"
        ),
        "Rezervasyonu İptal Et" to mapOf(
            AppLanguage.RU to "Аннулировать бронь",
            AppLanguage.TR to "Rezervasyonu İptal Et",
            AppLanguage.EN to "Rezervasyonu İptal Et"
        ),
        "Folyo & Harcamalar" to mapOf(
            AppLanguage.RU to "Фолио и начисления гостя",
            AppLanguage.TR to "Folyo & Harcamalar",
            AppLanguage.EN to "Folyo & Harcamalar"
        ),
        "Oda Ücreti" to mapOf(
            AppLanguage.RU to "Стоимость проживания",
            AppLanguage.TR to "Oda Ücreti",
            AppLanguage.EN to "Oda Ücreti"
        ),
        "Tahsilat Yap" to mapOf(
            AppLanguage.RU to "Принять оплату",
            AppLanguage.TR to "Tahsilat Yap",
            AppLanguage.EN to "Tahsilat Yap"
        ),
        "Check-in Yap" to mapOf(
            AppLanguage.RU to "Заселить гостя",
            AppLanguage.TR to "Check-in Yap",
            AppLanguage.EN to "Check-in Yap"
        ),
        "Check-out Yap" to mapOf(
            AppLanguage.RU to "Выселить и закрыть счет",
            AppLanguage.TR to "Check-out Yap",
            AppLanguage.EN to "Check-out Yap"
        ),
        "Gece Denetimi (Night Audit)" to mapOf(
            AppLanguage.RU to "Ночной аудит (Night Audit)",
            AppLanguage.TR to "Gece Denetimi (Night Audit)",
            AppLanguage.EN to "Gece Denetimi (Night Audit)"
        ),
        "Temiz" to mapOf(
            AppLanguage.RU to "Чистый (Clean)",
            AppLanguage.TR to "Temiz",
            AppLanguage.EN to "Temiz"
        ),
        "Kirli" to mapOf(
            AppLanguage.RU to "Грязный / Требует уборки (Dirty)",
            AppLanguage.TR to "Kirli",
            AppLanguage.EN to "Kirli"
        ),
        "Dolu" to mapOf(
            AppLanguage.RU to "Занят (Occupied)",
            AppLanguage.TR to "Dolu",
            AppLanguage.EN to "Dolu"
        ),
        "Boş" to mapOf(
            AppLanguage.RU to "Свободен (Vacant)",
            AppLanguage.TR to "Boş",
            AppLanguage.EN to "Boş"
        ),
        "Arızalı / Bakımda" to mapOf(
            AppLanguage.RU to "На ремонте / Вне эксплуатации (OOO)",
            AppLanguage.TR to "Arızalı / Bakımda",
            AppLanguage.EN to "Arızalı / Bakımda"
        ),
        "Temizlik Başlat" to mapOf(
            AppLanguage.RU to "Начать уборку",
            AppLanguage.TR to "Temizlik Başlat",
            AppLanguage.EN to "Temizlik Başlat"
        ),
        "Temizliği Tamamla" to mapOf(
            AppLanguage.RU to "Завершить уборку",
            AppLanguage.TR to "Temizliği Tamamla",
            AppLanguage.EN to "Temizliği Tamamla"
        ),
        "Bakıma Al" to mapOf(
            AppLanguage.RU to "Поставить на техобслуживание",
            AppLanguage.TR to "Bakıma Al",
            AppLanguage.EN to "Bakıma Al"
        ),
        "Arıza Bildirimi Oluştur" to mapOf(
            AppLanguage.RU to "Создать заявку инженерной службе",
            AppLanguage.TR to "Arıza Bildirimi Oluştur",
            AppLanguage.EN to "Arıza Bildirimi Oluştur"
        ),
        "Bu Odadaki Geçmiş Kayıtlar" to mapOf(
            AppLanguage.RU to "История обслуживания и ремонтов номера",
            AppLanguage.TR to "Bu Odadaki Geçmiş Kayıtlar",
            AppLanguage.EN to "Bu Odadaki Geçmiş Kayıtlar"
        ),
        "Durum Değiştir" to mapOf(
            AppLanguage.RU to "Сменить статус номера",
            AppLanguage.TR to "Durum Değiştir",
            AppLanguage.EN to "Durum Değiştir"
        ),
        "Oda Kartı / Anahtarı" to mapOf(
            AppLanguage.RU to "Электронный ключ от номера",
            AppLanguage.TR to "Oda Kartı / Anahtarı",
            AppLanguage.EN to "Oda Kartı / Anahtarı"
        ),
        "Minibar Kontrolü" to mapOf(
            AppLanguage.RU to "Проверка мини-бара",
            AppLanguage.TR to "Minibar Kontrolü",
            AppLanguage.EN to "Minibar Kontrolü"
        ),
        "Kayıp Eşya (Lost & Found)" to mapOf(
            AppLanguage.RU to "Бюро находок (Lost & Found)",
            AppLanguage.TR to "Kayıp Eşya (Lost & Found)",
            AppLanguage.EN to "Kayıp Eşya (Lost & Found)"
        ),
        "Kasa Hareketleri" to mapOf(
            AppLanguage.RU to "Движение денежных средств (ДДС)",
            AppLanguage.TR to "Kasa Hareketleri",
            AppLanguage.EN to "Kasa Hareketleri"
        ),
        "Nakit Girişi" to mapOf(
            AppLanguage.RU to "Приходный кассовый ордер (ПКО)",
            AppLanguage.TR to "Nakit Girişi",
            AppLanguage.EN to "Nakit Girişi"
        ),
        "Nakit Çıkışı" to mapOf(
            AppLanguage.RU to "Расходный кассовый ордер (РКО)",
            AppLanguage.TR to "Nakit Çıkışı",
            AppLanguage.EN to "Nakit Çıkışı"
        ),
        "Cari & Fatura" to mapOf(
            AppLanguage.RU to "Взаиморасчеты с контрагентами и счета",
            AppLanguage.TR to "Cari & Fatura",
            AppLanguage.EN to "Cari & Fatura"
        ),
        "Cari Hesaplar & Faturalar" to mapOf(
            AppLanguage.RU to "Счета контрагентов и закрывающие документы",
            AppLanguage.TR to "Cari Hesaplar & Faturalar",
            AppLanguage.EN to "Cari Hesaplar & Faturalar"
        ),
        "Cari Net" to mapOf(
            AppLanguage.RU to "Чистое сальдо взаиморасчетов",
            AppLanguage.TR to "Cari Net",
            AppLanguage.EN to "Cari Net"
        ),
        "Bakiye" to mapOf(
            AppLanguage.RU to "Остаток / Баланс",
            AppLanguage.TR to "Bakiye",
            AppLanguage.EN to "Bakiye"
        ),
        "Borç" to mapOf(
            AppLanguage.RU to "Дебиторская задолженность (Дебет)",
            AppLanguage.TR to "Borç",
            AppLanguage.EN to "Borç"
        ),
        "Alacak" to mapOf(
            AppLanguage.RU to "Кредиторская задолженность (Кредит)",
            AppLanguage.TR to "Alacak",
            AppLanguage.EN to "Alacak"
        ),
        "Fatura Kes" to mapOf(
            AppLanguage.RU to "Выставить счет-фактуру / Акт",
            AppLanguage.TR to "Fatura Kes",
            AppLanguage.EN to "Fatura Kes"
        ),
        "Tahsilat" to mapOf(
            AppLanguage.RU to "Поступление оплаты",
            AppLanguage.TR to "Tahsilat",
            AppLanguage.EN to "Tahsilat"
        ),
        "Ödeme" to mapOf(
            AppLanguage.RU to "Выплата / Платеж",
            AppLanguage.TR to "Ödeme",
            AppLanguage.EN to "Ödeme"
        ),
        "Kasa Kapatma (Z-Raporu)" to mapOf(
            AppLanguage.RU to "Закрытие кассовой смены (Z-отчет)",
            AppLanguage.TR to "Kasa Kapatma (Z-Raporu)",
            AppLanguage.EN to "Kasa Kapatma (Z-Raporu)"
        ),
        "Gelir / Gider Grafiği" to mapOf(
            AppLanguage.RU to "График доходов и расходов",
            AppLanguage.TR to "Gelir / Gider Grafiği",
            AppLanguage.EN to "Gelir / Gider Grafiği"
        ),
        "Gelir / Gider Analizi" to mapOf(
            AppLanguage.RU to "Анализ структуры доходов и расходов",
            AppLanguage.TR to "Gelir / Gider Analizi",
            AppLanguage.EN to "Gelir / Gider Analizi"
        ),
        "KDV / Vergi Matrahı" to mapOf(
            AppLanguage.RU to "НДС и налоговая база",
            AppLanguage.TR to "KDV / Vergi Matrahı",
            AppLanguage.EN to "KDV / Vergi Matrahı"
        ),
        "Banka Hesapları" to mapOf(
            AppLanguage.RU to "Расчетные счета отеля",
            AppLanguage.TR to "Banka Hesapları",
            AppLanguage.EN to "Banka Hesapları"
        ),
        "Fiyatlandırma Kuralları (Mark-up)" to mapOf(
            AppLanguage.RU to "Тарифные правила и наценки (Mark-up)",
            AppLanguage.TR to "Fiyatlandırma Kuralları (Mark-up)",
            AppLanguage.EN to "Fiyatlandırma Kuralları (Mark-up)"
        ),
        "CSV İle Toplu Fiyat Aktar" to mapOf(
            AppLanguage.RU to "Массовый импорт тарифов через CSV",
            AppLanguage.TR to "CSV İle Toplu Fiyat Aktar",
            AppLanguage.EN to "CSV İle Toplu Fiyat Aktar"
        ),
        "CSV İçeriği" to mapOf(
            AppLanguage.RU to "Содержимое файла CSV",
            AppLanguage.TR to "CSV İçeriği",
            AppLanguage.EN to "CSV İçeriği"
        ),
        "CSV Aktar" to mapOf(
            AppLanguage.RU to "Импортировать CSV",
            AppLanguage.TR to "CSV Aktar",
            AppLanguage.EN to "CSV Aktar"
        ),
        "AI Önerisi: Fiyatları Artırın" to mapOf(
            AppLanguage.RU to "Рекомендация ИИ: Повысить тариф на 15%",
            AppLanguage.TR to "AI Önerisi: Fiyatları Artırın",
            AppLanguage.EN to "AI Önerisi: Fiyatları Artırın"
        ),
        "Aktif Fiyatlandırma Kuralları" to mapOf(
            AppLanguage.RU to "Действующие правила тарифов",
            AppLanguage.TR to "Aktif Fiyatlandırma Kuralları",
            AppLanguage.EN to "Aktif Fiyatlandırma Kuralları"
        ),
        "DİKKAT: OVERBOOKING TESPİT EDİLDİ!" to mapOf(
            AppLanguage.RU to "ВНИМАНИЕ: ОБНАРУЖЕН ОВЕРБУКИНГ!",
            AppLanguage.TR to "DİKKAT: OVERBOOKING TESPİT EDİLDİ!",
            AppLanguage.EN to "DİKKAT: OVERBOOKING TESPİT EDİLDİ!"
        ),
        "Acenta Seçiniz:" to mapOf(
            AppLanguage.RU to "Выберите туроператора / агентство:",
            AppLanguage.TR to "Acenta Seçiniz:",
            AppLanguage.EN to "Acenta Seçiniz:"
        ),
        "Acenta bulunamadı." to mapOf(
            AppLanguage.RU to "Агентство не найдено в справочнике.",
            AppLanguage.TR to "Acenta bulunamadı.",
            AppLanguage.EN to "Acenta bulunamadı."
        ),
        "OTA Entegrasyonları (Booking, Ostrovok, Yandex)" to mapOf(
            AppLanguage.RU to "Интеграция с ОТА (Яндекс Путешествия, Островок, Броневик)",
            AppLanguage.TR to "OTA Entegrasyonları (Booking, Ostrovok, Yandex)",
            AppLanguage.EN to "OTA Entegrasyonları (Booking, Ostrovok, Yandex)"
        ),
        "Raporlar" to mapOf(
            AppLanguage.RU to "Управленческая отчетность",
            AppLanguage.TR to "Raporlar",
            AppLanguage.EN to "Raporlar"
        ),
        "Acenta Raporu" to mapOf(
            AppLanguage.RU to "Отчет по продажам туроператоров",
            AppLanguage.TR to "Acenta Raporu",
            AppLanguage.EN to "Acenta Raporu"
        ),
        "Acenta Bazında Müşteri Dağılımı" to mapOf(
            AppLanguage.RU to "Сегментация гостей по каналам продаж",
            AppLanguage.TR to "Acenta Bazında Müşteri Dağılımı",
            AppLanguage.EN to "Acenta Bazında Müşteri Dağılımı"
        ),
        "Aylık Satış Metrikleri" to mapOf(
            AppLanguage.RU to "Ежемесячные ключевые метрики продаж",
            AppLanguage.TR to "Aylık Satış Metrikleri",
            AppLanguage.EN to "Aylık Satış Metrikleri"
        ),
        "Genel Satış Özeti" to mapOf(
            AppLanguage.RU to "Сводный отчет по доходам (RevPAR / ADR)",
            AppLanguage.TR to "Genel Satış Özeti",
            AppLanguage.EN to "Genel Satış Özeti"
        ),
        "Gerçekleşen Rezervasyon Sayısı:" to mapOf(
            AppLanguage.RU to "Количество реализованных броней:",
            AppLanguage.TR to "Gerçekleşen Rezervasyon Sayısı:",
            AppLanguage.EN to "Gerçekleşen Rezervasyon Sayısı:"
        ),
        "Doluluk oranı" to mapOf(
            AppLanguage.RU to "Коэффициент загрузки отеля (Occupancy)",
            AppLanguage.TR to "Doluluk oranı",
            AppLanguage.EN to "Doluluk oranı"
        ),
        "ADR (Ortalama Günlük Fiyat)" to mapOf(
            AppLanguage.RU to "ADR (Средняя цена за номер в сутки)",
            AppLanguage.TR to "ADR (Ortalama Günlük Fiyat)",
            AppLanguage.EN to "ADR (Ortalama Günlük Fiyat)"
        ),
        "RevPAR (Oda Başına Gelir)" to mapOf(
            AppLanguage.RU to "RevPAR (Доход на доступный номер)",
            AppLanguage.TR to "RevPAR (Oda Başına Gelir)",
            AppLanguage.EN to "RevPAR (Oda Başına Gelir)"
        ),
        "Gelecek Dönem Tahmini" to mapOf(
            AppLanguage.RU to "Прогноз загрузки на будущий период",
            AppLanguage.TR to "Gelecek Dönem Tahmini",
            AppLanguage.EN to "Gelecek Dönem Tahmini"
        ),
        "Bu tarih aralığında acenta rezervasyonu bulunamadı." to mapOf(
            AppLanguage.RU to "За выбранный период бронирований от агентств не найдено.",
            AppLanguage.TR to "Bu tarih aralığında acenta rezervasyonu bulunamadı.",
            AppLanguage.EN to "Bu tarih aralığında acenta rezervasyonu bulunamadı."
        ),
        "Kara Liste (Blacklist)" to mapOf(
            AppLanguage.RU to "Черный список нежелательных гостей",
            AppLanguage.TR to "Kara Liste (Blacklist)",
            AppLanguage.EN to "Kara Liste (Blacklist)"
        ),
        "Boşsa otomatik üretilir" to mapOf(
            AppLanguage.RU to "Оставьте пустым для автогенерации",
            AppLanguage.TR to "Boşsa otomatik üretilir",
            AppLanguage.EN to "Boşsa otomatik üretilir"
        ),
        "Düzenle" to mapOf(
            AppLanguage.RU to "Редактировать",
            AppLanguage.TR to "Düzenle",
            AppLanguage.EN to "Düzenle"
        ),
        "Filtrele" to mapOf(
            AppLanguage.RU to "Применить фильтры",
            AppLanguage.TR to "Filtrele",
            AppLanguage.EN to "Filtrele"
        ),
        "Ara..." to mapOf(
            AppLanguage.RU to "Поиск по базе данных...",
            AppLanguage.TR to "Ara...",
            AppLanguage.EN to "Ara..."
        ),
        "Yenile" to mapOf(
            AppLanguage.RU to "Обновить данные",
            AppLanguage.TR to "Yenile",
            AppLanguage.EN to "Yenile"
        ),
    )

    fun translate(text: String, lang: AppLanguage = _currentLanguage.value): String {
        val trimmed = text.trim()
        val directMatch = translationDictionary[trimmed]
        if (directMatch != null) {
            return directMatch[lang] ?: directMatch[AppLanguage.RU] ?: directMatch[AppLanguage.TR] ?: directMatch[AppLanguage.EN] ?: trimmed
        }

        // Smart compound translation for hyphenated strings (e.g., "Restoran Geliri - 101")
        if (trimmed.contains(" - ")) {
            val parts = trimmed.split(" - ", limit = 2)
            val translatedPrefix = translate(parts[0], lang)
            if (translatedPrefix != parts[0]) {
                return "$translatedPrefix - ${parts[1]}"
            }
        }

        // Smart translation for room labels (e.g. "Oda 107" -> "Номер 107")
        if (trimmed.startsWith("Oda ") && trimmed.length > 4) {
            val roomNo = trimmed.substring(4)
            val odaTrans = translate("Oda", lang)
            return "$odaTrans $roomNo"
        }

        // Smart translation for table labels (e.g. "Masa 4" -> "Стол 4", "Bahçe 1" -> "Сад 1", "Teras 2" -> "Терраса 2")
        if (trimmed.startsWith("Masa ") && trimmed.length > 5) {
            val no = trimmed.substring(5)
            val masaTrans = translate("Masa", lang)
            return "$masaTrans $no"
        }
        if (trimmed.startsWith("Bahçe ") && trimmed.length > 6) {
            val no = trimmed.substring(6)
            val bahceTrans = translate("Bahçe", lang)
            return "$bahceTrans $no"
        }
        if (trimmed.startsWith("Teras ") && trimmed.length > 6) {
            val no = trimmed.substring(6)
            val terasTrans = translate("Teras", lang)
            return "$terasTrans $no"
        }

        return trimmed
    }
}

/**
 * Dynamic composable helper to localize any string live in Compose UI
 */
@Composable
fun l10n(turkishOrOriginalText: String): String {
    val currentLang by AppLocalizationManager.currentLanguage.collectAsState()
    return AppLocalizationManager.translate(turkishOrOriginalText, currentLang)
}

